/**
 * Payments ticking their own boxes.
 *
 * Two Edge Functions record facts — `quickbooks payments` and `mercury
 * deposits` — and everything in this file turns those facts into ticks. The
 * deciding is here, in the browser, rather than in Deno, because the rule needs
 * the payment schedule and the split threshold and those live in
 * `packages/core` where the tests are (CLAUDE.md rule 5). An Edge Function that
 * worked out which payment an amount was would be a second implementation of
 * the schedule, out of reach of every test that pins it.
 *
 * The two halves are deliberately not symmetrical, and that is Jonathon's call
 * (2026-08-31):
 *
 * - **A QuickBooks payment applies itself.** It arrives carrying the invoice
 *   id, which names exactly one job, so there is nothing to guess and nothing
 *   to ask. It ticks "First Check Submitted" or "Final Check Submitted".
 * - **A Mercury deposit waits for a person.** A bank line is an amount and the
 *   words "QUICKBOOKS PAYMENTS DEPOSIT". Somebody confirms which job it is, and
 *   that confirmation ticks "First Check" or "Final Check" — the box that takes
 *   a job off the ongoing board and moves its stage to complete.
 *
 * Polling is on demand, the way storm-poll is: opening the ongoing board asks
 * both services what has happened. There is no scheduler in this project
 * (docs/DEPLOYMENT.md), and both polls are safe to run as often as anybody
 * likes — the unique ids on both tables mean a repeated poll records nothing
 * twice.
 */

import {
  CHECK_IN_THE_MAIL_DAYS,
  type BankLine,
  type DepositLink,
  type PaidRequest,
  type PayoutCharge,
  buildReceipt,
  checkPayerName,
  checksSettledByBox,
  checksTheBankHolds,
  checksUnsettledByBox,
  clearedBoxChange,
  type JobChecklistItem,
  chargesAccountForTheLine,
  chargesToRequests,
  invoiceNumberFor,
  linkBankLines,
  payoutNeedsSplitting,
  settlesWithoutAPerson,
  payoutOnTheStatement,
  payoutsFromCharges,
  recordedPaymentThisCreditIs,
  recordedFromClearedOffer,
  requestThisCreditPays,
  CUSTOMER_PAYMENT_METHOD_LABEL,
  PAYMENT_CHECKLIST_LABELS,
  type CheckCandidate,
  type CustomerPaymentMethod,
  type CustomerPaymentState,
  identifyCheck,
  creditThisPaymentIs,
  isCheckDeposit,
  jobBalance,
  labelsOnClearing,
  MARKED_IN_THE_BANK_METHODS,
  mayMarkInTheBank,
  recordForLink,
  paymentHistory,
  type PaymentHistoryRow,
  labelsOnRecording,
  receiptEmail,
  receiptFilename,
  receiptSender,
  sameName,
  stateOnRecording,
  type Cents,
  type MatchOptions,
  type PaymentMatch,
  type PaymentSlot,
  cents,
  expectedForSlots,
  formatMoney,
  labelsFor,
  type NumericFromDb,
  RULE_EARNED_AFTER,
  expectedPayments,
  learningNotice,
  normaliseCounterparty,
  timesAfterUndo,
  setAsideVerdict,
  type DepositRule,
  matchPayment,
  jobPlanStanding,
  mayMarkPlanPaymentBanked,
  mayReceipt,
  RECEIPT_REFUSED_FOR_LINK,
  linksWhoseCreditLanded,
  mayPaySlot,
  mayTickClearedBox,
  middlePaymentFit,
  PAYMENT_SLOT_LABEL,
  PLAN_FINAL_CHECK_REFUSED,
  PLAN_FINAL_REFUSED,
  settledOnPlan,
  paymentsOnOffer,
  processingFeeExpense,
  slotHasCheckboxes,
  splitLineNote,
  type SplitLine,
  type SplitPlan,
  suggestionDistance,
  toCents,
  toDecimal,
} from "@roof-hawk/core";

import { sendEmail } from "./gmail";
import {
  fetchCompanySettings,
  fetchJob,
  fetchJobBalanceInput,
  fetchJobPlans,
  fetchSignedContractBases,
  fileEmailedPdf,
  insertRow,
  planBalanceInput,
  signedContractTotal,
  updateRow,
} from "./queries";
import { receiptPdfBase64 } from "./receiptPdf";
import { supabase, unwrap } from "./supabase";

/** A payment QuickBooks has recorded against an invoice we sent. */
export interface QuickbooksPaymentRow {
  id: string;
  job_id: string;
  quickbooks_payment_id: string;
  amount_cents: number;
  txn_date: string | null;
  state: "new" | "applied" | "unmatched";
  slots: number[];
  unmatched_reason: string | null;
  seen_at: string;
  applied_at: string | null;
}

/** Money that landed in the Mercury account. */
export interface MercuryDepositRow {
  id: string;
  mercury_transaction_id: string;
  amount_cents: number;
  counterparty: string | null;
  description: string | null;
  posted_at: string | null;
  state: "new" | "confirmed" | "dismissed";
  job_id: string | null;
  slots: number[];
  confirmed_at: string | null;
  dismissed_reason: string | null;
  /** The batched payout this was carved out of. Null on an ordinary deposit. */
  parent_deposit_id?: string | null;
  /** What the card was charged for this part, gross. Null unless it is a part. */
  charge_cents?: number | null;
  /** Everything else Mercury said about the line, kept since 2026-09-12. */
  kind?: string | null;
  external_memo?: string | null;
  note?: string | null;
  tracking_number?: string | null;
  raw?: Record<string, unknown> | null;
}

/** What a job's payments are measured against. */
export interface JobPaymentContext {
  jobId: string;
  contractCents: Cents;
  firstCents: Cents | null;
  /**
   * What the customer signed, before any change order signed since.
   *
   * Only the deposit's default reads it, and only so that default cannot move:
   * a change order approved after the deposit arrived adds its whole amount to
   * the Final, and the figure a later payment is matched against stays the
   * figure the customer was billed (ADR 0205).
   */
  signedContractCents: Cents | null;
  firstCollected: boolean;
  /**
   * The 3% added to a slot because the customer paid it on a credit card.
   *
   * Without it a card payment arrives for MORE than the slot was worth, matches
   * nothing, and — worse — `processingFeeExpense` sees no shortfall and stops
   * recording Stripe's cut as a job cost on exactly the payments that have one.
   */
  surchargeBySlot: Record<number, Cents>;
  /**
   * The middle draws, slot 2 and slot 3, when the rep typed any.
   *
   * Most jobs have none. On the ones that do they decide two things: what the
   * final payment is (the contract less all three typed slots) and which
   * payments a person may choose from when money arrives.
   */
  middleCents: Record<number, Cents>;
  /** The checklist rows this file may tick, by label. */
  items: Map<string, { id: string; status: string }>;
}

/**
 * Everything needed to say what an amount means on one job.
 *
 * The contract comes from `quickbooks_invoices.amount_cents` where there is
 * one: that is the number QuickBooks is actually billing, worked out by
 * `invoiceAmount()` when the job was sold, and matching a payment against
 * anything else would mean matching it against a figure the customer never saw.
 *
 * **And from what they signed where there is not.** A job only gets a
 * QuickBooks invoice when somebody presses Sends, and from 2026-09-06 a
 * customer is asked to pay the moment they sign — so the ordinary case is now a
 * signed job with no invoice yet, whose payment could not be matched to
 * anything at all. Michael Lewis on job 1033 was the one Jonathon noticed:
 * signed, not sent, and simply not on the list.
 *
 * `proposal_acceptances.total` is what he agreed to, which is the same figure
 * the invoice would carry when it is raised.
 */
export async function fetchJobPaymentContext(
  jobId: string,
): Promise<JobPaymentContext | null> {
  const [invoice, schedule, checklist, surcharges] = await Promise.all([
    supabase
      .from("quickbooks_invoices")
      .select("amount_cents")
      .eq("job_id", jobId)
      .maybeSingle(),
    // Every typed slot. Slot 1 is the first payment; 2 and 3 are the draws,
    // and the final is what all three leave behind (`expectedPayments`).
    supabase
      .from("estimate_payments")
      .select("slot, amount, estimates!inner(job_id, is_active)")
      .eq("estimates.job_id", jobId)
      .eq("estimates.is_active", true)
      .in("slot", [1, 2, 3]),
    supabase
      .from("job_checklist_items")
      .select("id,label,status")
      .eq("job_id", jobId)
      .eq("phase", "production"),
    // Every slot the customer put on a card. Not filtered to `paid`, unlike
    // the ladder's copy: this is what we EXPECT to arrive, and the expectation
    // exists from the moment the invoice is raised. The ladder counts money
    // that landed; this counts money that was asked for.
    supabase
      .from("mercury_payment_requests")
      .select("slot,surcharge_cents,customer_method,state")
      .eq("job_id", jobId)
      .eq("customer_method", "card")
      .in("state", ["sent", "paid"]),
  ]);

  // The frozen signatures, whether or not there is an invoice: the QuickBooks
  // amount moves with a signed change order (ADR 0195) and this must not.
  const [signedContractCents, signedBase] = await Promise.all([
    signedContractTotal(jobId),
    fetchSignedContractBases([jobId]),
  ]);

  let amount = (invoice.data as { amount_cents: number | null } | null)
    ?.amount_cents;
  if (amount == null) {
    // `signedContractTotal` and not a second reader beside it: that column is
    // a decimal of DOLLARS, unlike `quickbooks_invoices.amount_cents` two lines
    // above, and getting the two the wrong way round is a hundredfold error in
    // a number that decides whether a customer still owes money.
    amount = signedContractCents;
  }
  if (amount == null || amount <= 0) return null;

  const typed = (schedule.data ?? []) as {
    slot: number | string;
    amount: number | string | null;
  }[];
  const first = typed.find((row) => Number(row.slot) === 1)?.amount;

  const middleCents: Record<number, Cents> = {};
  for (const row of typed) {
    const slot = Number(row.slot);
    if (slot === 1 || row.amount == null || row.amount === "") continue;
    // Dollars in this column, unlike `quickbooks_invoices.amount_cents` above.
    const amount = toCents(row.amount);
    if (amount > 0) middleCents[slot] = amount;
  }

  const surchargeBySlot: Record<number, Cents> = {};
  for (const row of (surcharges.data ?? []) as {
    slot: number | string;
    surcharge_cents: number | string | null;
  }[]) {
    const fee = Math.round(Number(row.surcharge_cents ?? 0));
    if (fee > 0) surchargeBySlot[Number(row.slot)] = cents(fee);
  }

  const items = new Map<string, { id: string; status: string }>();
  for (const row of (checklist.data ?? []) as {
    id: string;
    label: string;
    status: string;
  }[]) {
    items.set(row.label, { id: row.id, status: row.status });
  }

  return {
    jobId,
    contractCents: cents(amount),
    signedContractCents: signedBase.get(jobId) ?? signedContractCents,
    surchargeBySlot,
    middleCents,
    // `estimate_payments.amount` is a decimal column, so it is dollars here and
    // toCents is right — unlike `quickbooks_invoices.amount_cents` above, which
    // is already cents. Getting these two the wrong way round is a hundredfold
    // error in a number that ticks a box.
    firstCents: first == null || first === "" ? null : toCents(first),
    firstCollected: items.get("First Check")?.status === "done",
    items,
  };
}

/**
 * The match for one amount on one job, and the labels it would tick.
 *
 * Say `netOfFees` for a bank deposit, which is what was left after the
 * processor's cut. Say nothing for a QuickBooks payment, which is what the
 * customer paid before any fee came out, and so has to match to the cent.
 */
export function matchOnJob(
  amountCents: number,
  context: JobPaymentContext,
  options: MatchOptions = {},
): PaymentMatch {
  return matchPayment(
    cents(amountCents),
    {
      contractCents: context.contractCents,
      firstCents: context.firstCents,
      signedContractCents: context.signedContractCents,
      middleCents: context.middleCents,
      firstCollected: context.firstCollected,
      surchargeBySlot: context.surchargeBySlot,
    },
    options,
  );
}

/**
 * Tick the boxes a match names, and say so on the job.
 *
 * A box already ticked is left alone rather than re-stamped: somebody may have
 * ticked it by hand this morning, and overwriting who did it with "nobody" is a
 * worse record than the one we had.
 *
 * The note is written as an **internal** activity on purpose. It is staff
 * bookkeeping, not contact with the customer, and internal traffic does not
 * touch the follow-up clock (docs/BUSINESS-RULES.md).
 */
async function tick(
  context: JobPaymentContext,
  labels: readonly string[],
  note: string,
): Promise<string[]> {
  const ticked: string[] = [];
  for (const label of labels) {
    const item = context.items.get(label);
    if (!item || item.status === "done") continue;
    await updateRow("job_checklist_items", item.id, {
      status: "done",
      completed_at: new Date().toISOString(),
      // Nobody: this was not a person, and recording a person would be a lie
      // on a screen that names who ticked each box.
      completed_by: null,
    });
    item.status = "done";
    ticked.push(label);
  }

  if (ticked.length > 0) {
    await insertRow("activities", {
      job_id: context.jobId,
      type: "note",
      direction: "internal",
      body: `${note} — ticked ${ticked.join(" and ")}.`,
    });
  }
  return ticked;
}

/**
 * Apply every QuickBooks payment nobody has looked at yet.
 *
 * Returns what it did, so the screen that called it can say so. A payment whose
 * amount is not one of the three the job expects is marked `unmatched` with the
 * reason — it is not retried on the next poll, because the answer would be the
 * same, and it is not silently dropped either.
 */
export async function applyQuickbooksPayments(): Promise<{
  applied: number;
  unmatched: number;
}> {
  const rows = unwrap<QuickbooksPaymentRow[]>(
    await supabase
      .from("quickbooks_payments")
      .select("*")
      .eq("state", "new")
      .order("seen_at")
      .limit(50),
  );

  let applied = 0;
  let unmatched = 0;
  const contexts = new Map<string, JobPaymentContext | null>();

  for (const row of rows) {
    if (!contexts.has(row.job_id)) {
      contexts.set(row.job_id, await fetchJobPaymentContext(row.job_id));
    }
    const context = contexts.get(row.job_id) ?? null;

    if (!context) {
      await updateRow("quickbooks_payments", row.id, {
        state: "unmatched",
        unmatched_reason:
          "This job has no invoice amount in Roof Board, so there is nothing to match the payment against.",
      });
      unmatched += 1;
      continue;
    }

    const match = matchOnJob(row.amount_cents, context);
    if (!match.matched) {
      await updateRow("quickbooks_payments", row.id, {
        state: "unmatched",
        unmatched_reason: match.why,
      });
      unmatched += 1;
      continue;
    }

    await tick(
      context,
      labelsFor(match, "submitted"),
      `Payment taken in QuickBooks${row.txn_date ? ` on ${row.txn_date}` : ""}: ${match.why}`,
    );
    await updateRow("quickbooks_payments", row.id, {
      state: "applied",
      slots: match.slots,
      applied_at: new Date().toISOString(),
    });
    applied += 1;
  }

  return { applied, unmatched };
}

/**
 * Confirm a bank deposit against a job, and tick the money-is-here boxes.
 *
 * The match is recomputed here rather than trusted from the screen: the screen
 * may have been open for an hour, and in that hour somebody may have ticked
 * First Check by hand, which changes what this deposit is.
 */
export async function confirmDeposit(
  deposit: MercuryDepositRow,
  jobId: string,
  profileId: string | null,
  /**
   * What identified this money.
   *
   * `"person"` is somebody pressing the button. `"quickbooks"` is Kelsey having
   * already matched the payment to a customer there, which is the proof of
   * ownership a bank line on its own never carries — see
   * `closeDepositsQuickbooksHasIdentified`. `"check"` is a check deposit
   * clearing that was already recorded against this job — by a rep, or by
   * Roof Board recognising the name on it (ADR 0125). `"mercury"` is the bank
   * line naming a payment request Roof Board raised, by an identifier on the
   * line and never by an amount or a name (ADR 0142). `"stripe"` is a card
   * charge Stripe named inside a payout, by the email it billed — a different
   * fact, kept apart so the job's history can say which one happened.
   */
  source: "person" | "quickbooks" | "check" | "mercury" | "stripe" = "person",
  /**
   * Overrides, for a deposit whose meaning is known from outside the amount.
   *
   * Both exist for a part of a batched payout and nothing else. A split part's
   * amount is a share of one bank line, so the two things the arithmetic
   * normally reads off the amount are not in it:
   *
   * - `slot` is which payment a person said this part settles, needed because
   *   on the default 50/50 schedule the first and the final are the same
   *   number and `matchPayment` refuses rather than guesses;
   * - `chargeCents` is what the customer's card was charged, which is what the
   *   job was owed for this part. Without it the fee would be measured against
   *   the whole slot and a part payment would be booked as a colossal fee.
   */
  overrides: {
    slot?: PaymentSlot | null | undefined;
    chargeCents?: Cents | null;
    /**
     * Where an asserted slot came from, because the sentence this writes on
     * the job's history has to be true of what happened: a share of a batched
     * payout, or a whole bank line tied to the request it settles.
     */
    because?: "split" | "request";
    /**
     * Whether this customer is to hear about it at all. Checks only.
     *
     * A different kind of thing from the two above, and the only one a person
     * chooses. `false` draws the receipt and files it on the job without ever
     * putting it in front of anybody: the customer has one already, by hand or
     * from their own bank, and it should not land on somebody's list asking to
     * be sent. Ticked — the default — it waits to be read and sent like every
     * other receipt (ADR 0140).
     *
     * Jonathon, 2026-09-14, after job 1023's receipt went out the instant he
     * matched the deposit. The review prompt answers the "silently" half of
     * that; this answers the rest, which is a receipt that should not go at
     * all.
     */
    receipt?: boolean | undefined;
  } = {},
): Promise<{ ticked: string[]; why: string }> {
  const context = await fetchJobPaymentContext(jobId);
  if (!context) {
    throw new Error(
      "This job has no invoice amount in Roof Board yet, so a payment cannot be matched to it.",
    );
  }

  const matched = overrides.slot
    ? saidByAPerson(
        overrides.slot,
        cents(deposit.amount_cents),
        overrides.because ?? "split",
      )
    : matchOnJob(deposit.amount_cents, context, { netOfFees: true });
  // On an in-house payment plan the Final is paid month by month, so no
  // deposit settles it whole: one that would is money toward the plan, ticks
  // nothing, and counts at its own amount (ADR 0286).
  const match = settledOnPlan(matched, await jobIsOnPlan(jobId)).match;

  /*
   * An amount that fits nothing is recorded anyway, and ticks nothing.
   *
   * It threw until 2026-09-06, which left the money on the list forever: a
   * customer who pays an odd amount, pays half of half, or sends the balance in
   * two goes could not be recorded against their own job at all. Jonathon asked
   * for the job to be choosable regardless.
   *
   * The two questions are separate and only the first is a person's to answer.
   * **Whose money is this** — always theirs to say. **Which scheduled payment
   * does it settle** — arithmetic, and if the amount settles none of them then
   * none is ticked. The job keeps the record and the boxes keep their meaning.
   */
  const ticked = await tick(
    context,
    labelsFor(match, "cleared"),
    `Money arrived in Mercury${deposit.posted_at ? ` on ${deposit.posted_at.slice(0, 10)}` : ""}: ${match.why}` +
      (source === "quickbooks"
        ? " Matched from QuickBooks, where this payment was already recorded against the customer."
        : source === "check"
          ? " The check recorded against this job has cleared."
          : source === "mercury"
            ? " The bank line names the payment request Roof Board sent this customer."
            : source === "stripe"
              ? " Stripe says this charge was inside the payout that landed, billed to this customer's email."
              : ""),
  );

  // `tick` writes the job's history only when it ticks something, so money
  // that settled no scheduled payment would otherwise land on the job with no
  // trace of why it is there.
  if (ticked.length === 0) {
    await insertRow("activities", {
      job_id: jobId,
      type: "note",
      direction: "internal",
      body:
        `${formatMoney(cents(deposit.amount_cents))} arrived in Mercury and was ` +
        `recorded against this job. ${match.why} No payment box was ticked.`,
    });
  }

  // What it cost to receive. The difference between what the job was owed for
  // the slots this ticked and what actually reached the bank is the processor's
  // fee, and it is a cost of doing the job (ADR 0095).
  const expected =
    overrides.chargeCents != null
      ? cents(overrides.chargeCents)
      : expectedForSlots(match.slots, context);
  const fee = processingFeeExpense({
    expected,
    received: cents(deposit.amount_cents),
    description: deposit.description,
  });

  let feeExpenseId: string | null = null;
  if (fee) {
    // Onto the active estimate, not merely the job: the ladder reads a job's
    // expenses by estimate_id, so a fee with no estimate on it would sit in the
    // database counting towards nothing and the margin would be unchanged —
    // which is the whole thing this exists to fix.
    const estimateId = await activeEstimateId(jobId);
    if (estimateId) {
      feeExpenseId = await bookProcessingFeeOnce(deposit, {
        jobId,
        estimateId,
        name: fee.name,
        amount: fee.amount,
        expected,
      });
    }
  }

  // Before the confirmation, so a credit never counts on the job without
  // saying what it is the money for. A tie to a deposit that then fails to
  // confirm is harmless: every reader counts confirmed credits only, and the
  // retry finds the same tie.
  await tieCreditToWhatItPays(deposit, jobId, {
    chargedCents:
      overrides.chargeCents != null
        ? cents(overrides.chargeCents)
        : cents(Number(deposit.charge_cents ?? 0) || deposit.amount_cents),
    slots: match.slots,
    handedOverAlready:
      source === "check" ||
      isCheckDeposit({ description: deposit.description }),
  });

  await updateRow("mercury_deposits", deposit.id, {
    state: "confirmed",
    job_id: jobId,
    slots: match.slots,
    confirmed_by: profileId,
    confirmed_source: source,
    confirmed_at: new Date().toISOString(),
    expected_cents: expected,
    fee_cents: fee?.amount ?? 0,
    fee_expense_id: feeExpenseId,
  });

  /*
   * A check a person has just said is this customer's. It is paid now — it is
   * in the bank — and if nobody recorded it or receipted it, this is the
   * moment the customer hears (ADR 0125). After the confirmation and never
   * fatal to it: the money is matched either way.
   */
  if (
    source !== "check" &&
    isCheckDeposit({ description: deposit.description })
  ) {
    try {
      await checkConfirmedByAPerson(deposit, jobId, profileId, {
        review: overrides.receipt !== false,
      });
    } catch {
      // The job shows the payment without a receipt, and Resend is there.
    }
  }

  return { ticked, why: match.why };
}

/**
 * Say what a credit being confirmed on this job is the money for.
 *
 * Every reader of the balance — the job screen, the production board and the
 * letter — already credits a link at what it BILLED when a request points at
 * the credit, and counts nothing for a credit a recorded payment points at.
 * What was missing was anything writing those two ties outside the automatic
 * paths, so a person's confirmation left both empty (Josh, round 4 of #235):
 * $5,000 cash, banked, counted twice ($3,260 owed, not $8,260), and a card
 * link paid through a split counted its surcharge ($6,431.10, not $6,630).
 *
 * Which request, and which recorded payment, are decided in packages/core —
 * `requestThisCreditPays` and `recordedPaymentThisCreditIs`. This fetches the
 * job's rows and writes the answer, each write conditional on the row being
 * untied so it can never take money another credit already holds.
 */
async function tieCreditToWhatItPays(
  deposit: MercuryDepositRow,
  jobId: string,
  credit: {
    chargedCents: Cents;
    slots: readonly number[];
    /** A check deposit, whose payment `checkConfirmedByAPerson` ties. */
    handedOverAlready: boolean;
  },
): Promise<void> {
  const requests = unwrap<
    {
      id: string;
      state: string;
      slot: number | null;
      amount_cents: number | null;
      surcharge_cents: number | null;
      mercury_deposit_id: string | null;
    }[]
  >(
    await supabase
      .from("mercury_payment_requests")
      .select("id,state,slot,amount_cents,surcharge_cents,mercury_deposit_id")
      .eq("job_id", jobId),
  );
  const requestId = requestThisCreditPays(
    requests.map((row) => ({
      id: row.id,
      state: String(row.state),
      slot: row.slot == null ? null : Number(row.slot),
      amountCents: row.amount_cents == null ? null : Number(row.amount_cents),
      surchargeCents: Number(row.surcharge_cents ?? 0),
      depositId: row.mercury_deposit_id,
    })),
    { id: deposit.id, chargedCents: credit.chargedCents, slots: credit.slots },
  );
  if (requestId) {
    const held = requests.find((row) => row.id === requestId);
    if (held?.mercury_deposit_id === deposit.id) return;
    // Lost to somebody else's claim — or to the one-request-per-credit index
    // — and that money is theirs; this credit goes on to be what it
    // otherwise is.
    const claimed = await claimRequestForDeposit(requestId, deposit).catch(
      () => false,
    );
    if (claimed) return;
  }

  if (credit.handedOverAlready) return;
  const payments = unwrap<
    {
      id: string;
      state: string;
      amount_cents: number | null;
      slots: number[] | null;
      toward_plan: boolean | null;
      mercury_deposit_id: string | null;
      mercury_payment_request_id: string | null;
    }[]
  >(
    await supabase
      .from("customer_payments")
      .select(
        "id,state,amount_cents,slots,toward_plan,mercury_deposit_id," +
          "mercury_payment_request_id",
      )
      .eq("job_id", jobId)
      .eq("state", "paid")
      .order("created_at"),
  );
  const paymentId = recordedPaymentThisCreditIs(
    payments.map((row) => ({
      id: row.id,
      state: String(row.state),
      amountCents: row.amount_cents == null ? null : Number(row.amount_cents),
      slots: row.slots ?? [],
      towardPlan: row.toward_plan === true,
      depositId: row.mercury_deposit_id,
      mercuryRequestId: row.mercury_payment_request_id,
    })),
    { id: deposit.id, amountCents: deposit.amount_cents, slots: credit.slots },
  );
  if (!paymentId) return;
  if (payments.find((row) => row.id === paymentId)?.mercury_deposit_id) return;
  await supabase
    .from("customer_payments")
    .update({ mercury_deposit_id: deposit.id })
    .eq("id", paymentId)
    .is("mercury_deposit_id", null);
}

/**
 * A match a person asserted rather than one the arithmetic found.
 *
 * Only reachable from a split, where the amount is a share of a batched bank
 * line and so cannot say for itself which payment it is. The `why` names who
 * decided, because a box ticked on somebody's say-so and a box ticked because
 * the numbers agreed are different facts and the job's history should not
 * blur them.
 */
function saidByAPerson(
  slot: PaymentSlot,
  received: Cents,
  /**
   * Where the amount came from, because this sentence lands on the job's
   * history and has to be true of what happened. A split is one share of a
   * batched payout; a Mercury match is a whole bank line tied to the request
   * it settles, which is nothing to do with a payout covering several
   * customers — what both paths used to say (Josh, 2026-09-13).
   */
  because: "split" | "request" = "split",
): PaymentMatch {
  const name = (PAYMENT_SLOT_LABEL[slot] ?? `payment ${slot}`).toLowerCase();
  return {
    matched: true,
    slots: [slot],
    why:
      `${formatMoney(received)} was recorded as the ${name}` +
      (because === "split"
        ? ", out of a payout covering more than one customer."
        : ", against the payment request Mercury raised for it.") +
      // The middle draws have no boxes, and a person who has just chosen one
      // should be told that rather than left wondering why nothing moved.
      (slotHasCheckboxes(slot)
        ? ""
        : " The middle payments have no box on the production checklist, so" +
          " nothing was ticked."),
  };
}

/**
 * The processing fee for a bank line, booked once however often this runs.
 *
 * Josh, 2026-09-14, on #231: "a failed confirm followed by a retry can book
 * the card fee twice." Confirming a deposit writes the fee before it writes
 * the deposit's own `confirmed` state, so a confirmation that falls over in
 * between leaves the expense on the job and the money still on Money In, ready
 * to be confirmed again — and `confirmDepositForRequest` hands its claim back
 * on exactly that failure, so being confirmed again is the expected next step,
 * not a rare accident. A card fee on a $13,260 payment is around $400, and a
 * second one comes straight off the job's margin with nothing on any screen
 * saying why.
 *
 * So the bank line, not the attempt, is the key. `job_expenses` carries a
 * unique `mercury_deposit_id`, and this reads back the row that is already
 * there rather than writing another.
 *
 * Reading first and inserting second is two round trips, so the insert can
 * still lose a race with somebody else's. That is what the unique index is
 * for; when it fires, the fee this call wanted is already booked, and the
 * loser reads it back and reports the same id as the winner.
 *
 * It does not correct a fee already booked. The expense is an amount that left
 * the company, and if a retry measures it differently — a slot changed under
 * it, say — the first answer is the one a person can see and edit on the job.
 */
async function bookProcessingFeeOnce(
  deposit: MercuryDepositRow,
  fee: {
    jobId: string;
    estimateId: string;
    name: string;
    amount: Cents;
    expected: Cents;
  },
): Promise<string | null> {
  const already = await existingFeeExpense(deposit.id);
  if (already) return already;

  try {
    const created = await insertRow<{ id: string }>("job_expenses", {
      job_id: fee.jobId,
      estimate_id: fee.estimateId,
      name: fee.name,
      amount: toDecimal(fee.amount),
      occurred_at: deposit.posted_at ?? new Date().toISOString(),
      note: `${formatMoney(fee.expected)} expected, ${formatMoney(cents(deposit.amount_cents))} arrived.`,
      is_processing_fee: true,
      mercury_deposit_id: deposit.id,
    });
    return created.id;
  } catch (e) {
    // Somebody else got there between the read and the write. Theirs is the
    // fee; anything else that went wrong is still an error.
    const theirs = await existingFeeExpense(deposit.id);
    if (theirs) return theirs;
    throw e;
  }
}

/** The fee expense already booked against a bank line, if there is one. */
async function existingFeeExpense(depositId: string): Promise<string | null> {
  const rows = unwrap<{ id: string }[]>(
    await supabase
      .from("job_expenses")
      .select("id")
      .eq("mercury_deposit_id", depositId)
      .limit(1),
  );
  return rows[0]?.id ?? null;
}

/**
 * The estimate a fee belongs on: the job's active one.
 *
 * A job selling two options has two active estimates and the fee is one fee, so
 * the first is taken rather than split — splitting a $261.30 card charge across
 * two estimates would make neither of them true.
 */
async function activeEstimateId(jobId: string): Promise<string | null> {
  const rows = unwrap<{ id: string }[]>(
    await supabase
      .from("estimates")
      .select("id")
      .eq("job_id", jobId)
      .eq("is_active", true)
      .order("created_at")
      .limit(1),
  );
  return rows[0]?.id ?? null;
}

/**
 * Carve a batched payout into the payments that are actually inside it.
 *
 * Stripe pays out in sweeps: the $1,588.79 that reached Mercury on 2026-09-03
 * was Tyler Christiansen's $1,436.86 card charge and Jim Messer's $200.00, each
 * less 2.9% + 30c. Money In could only offer the whole line against one job.
 *
 * ## The order, which is the whole safety of it
 *
 * 1. every part is inserted and the payout is marked `split`, together, in one
 *    transaction — `split_mercury_deposit` in migration 20260908120000;
 * 2. each part is confirmed through the ordinary `confirmDeposit`, so it ticks
 *    the same boxes, writes the same history and records the same fee expense
 *    as a deposit that arrived on its own.
 *
 * Step 1 was two writes from here until 2026-09-08, in that order and for the
 * right reason — the parts exist before the payout leaves, so the money is
 * never off the screen. But between the two writes it was on the screen twice:
 * the parts on the Money In list and the payout still sitting there in 'new'
 * beside them, for its full amount. A dropped connection was all it took, and
 * nothing would have caught it — the parent trigger deliberately allows a
 * 'new' parent, because that is what a parent is while its parts go in, and
 * `fetchOpenDeposits` asks for exactly the state both of them are in. One
 * function is one transaction, so now there is no between.
 *
 * Step 2 stays here, where it belongs. If a part fails to confirm — a job
 * with no invoice amount, a lost connection — it simply stays on the Money In
 * list as its own deposit, for the right amount, waiting for a person. Nothing
 * is lost and nothing is doubled, which is the only failure mode worth
 * designing for here.
 *
 * The arithmetic is `planDepositSplit` in packages/core (CLAUDE.md rule 5) and
 * this refuses to run on a plan that is not `ready`: a split that does not add
 * up to the bank line is money recorded against a job that never arrived. The
 * function checks that sum again in the database, because an invariant about
 * money should not live only in the caller.
 */
export async function splitDeposit(
  deposit: MercuryDepositRow,
  plan: SplitPlan,
  profileId: string | null,
): Promise<{ recorded: number; failed: { label: string; why: string }[] }> {
  if (!plan.ready) {
    throw new Error(plan.problem ?? "This split does not add up yet.");
  }

  // One call, one transaction: every part exists and the payout is marked, or
  // the database is untouched. The rows come back in the order they went in,
  // which is the order of `plan.lines`.
  const { data, error } = await supabase.rpc("split_mercury_deposit", {
    p_parent: deposit.id,
    p_parts: plan.lines.map((line) => ({
      net_cents: line.netCents,
      gross_cents: line.grossCents,
    })),
  });
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as MercuryDepositRow[];
  if (rows.length !== plan.lines.length) {
    throw new Error(
      `The split recorded ${rows.length} parts, not ${plan.lines.length}.`,
    );
  }
  const children: { row: MercuryDepositRow; line: SplitLine }[] =
    plan.lines.map((line, index) => ({ row: rows[index]!, line }));

  let recorded = 0;
  const failed: { label: string; why: string }[] = [];
  for (const { row, line } of children) {
    if (!line.jobId) continue;
    try {
      await confirmDeposit(row, line.jobId, profileId, "person", {
        slot: line.slot,
        chargeCents: line.grossCents,
      });
      // Said on the job as well as counted, because the number on the job is
      // not the number on the bank statement and never will be.
      await insertRow("activities", {
        job_id: line.jobId,
        type: "note",
        direction: "internal",
        body: splitLineNote(line, deposit),
      });
      recorded += 1;
    } catch (e) {
      failed.push({
        label: formatMoney(line.netCents),
        why: e instanceof Error ? e.message : String(e),
      });
    }
  }

  return { recorded, failed };
}

/**
 * Ask a customer for an amount that is on no schedule (ADR 0141).
 *
 * The Edge Function raises the Mercury invoice and hands back the link; it
 * writes the request row, so nothing here can create a half-made one. Nothing
 * is sent to anybody — a link is a link, and a person decides who gets it.
 */
export async function raisePaymentLink(input: {
  jobId: string;
  amountCents: number;
  /**
   * The figure the person read back on "Are you sure?", captured when that
   * screen was shown — not `amountCents` again.
   *
   * The server refuses a link whose two figures differ (ADR 0141, Jonathon
   * 2026-09-13). Sending the same variable twice satisfied that from here
   * whatever the person saw, so the check proved nothing for the one screen
   * that actually raises links (Josh, round 4 of #235).
   */
  confirmedAmountCents: number;
  purpose: string;
  method: "card" | "ach";
}): Promise<{ paymentUrl: string; requestId: string; surchargeCents: number }> {
  const { data, error } = await supabase.functions.invoke("mercury", {
    body: {
      action: "link",
      jobId: input.jobId,
      amountCents: Math.round(input.amountCents),
      confirmAmountCents: Math.round(input.confirmedAmountCents),
      purpose: input.purpose,
      method: input.method,
    },
  });
  const answer = (data ?? {}) as {
    paymentUrl?: string;
    requestId?: string;
    surchargeCents?: number;
    error?: string;
  };
  if (answer.error) throw new Error(answer.error);
  if (error) throw new Error(error.message);
  if (!answer.paymentUrl || !answer.requestId) {
    throw new Error("Mercury did not give a link back.");
  }
  return {
    paymentUrl: answer.paymentUrl,
    requestId: answer.requestId,
    surchargeCents: Math.round(Number(answer.surchargeCents ?? 0)),
  };
}

/** Set a deposit aside — a supplier refund, a transfer, somebody else's money. */
export async function dismissDeposit(
  deposit: MercuryDepositRow,
  reason: string,
  profileId: string | null,
): Promise<{ notice: string | null }> {
  const said = reason.trim() || "Not a customer payment.";
  await updateRow("mercury_deposits", deposit.id, {
    state: "dismissed",
    dismissed_reason: said,
    // A person did this, not a rule. The two are told apart so the review list
    // only ever offers back what the software decided by itself.
    set_aside_automatically: false,
  });

  return { notice: await learnToSetAside(deposit, said, profileId) };
}

/**
 * Remember that this payer was set aside, and say so once it changes anything.
 *
 * The counting is here rather than in a trigger because the sentence a person
 * reads depends on the number it lands on, and that sentence is a business
 * decision (`learningNotice` in packages/core). A trigger would count silently
 * and the screen would start behaving differently with nobody told.
 *
 * Never throws. Setting a deposit aside must succeed even if the learning does
 * not — the worst case is being asked about the same payer again.
 */
async function learnToSetAside(
  deposit: MercuryDepositRow,
  reason: string,
  profileId: string | null,
): Promise<string | null> {
  const label = (deposit.counterparty ?? "").trim();
  const key = normaliseCounterparty(label);
  // A bank line with no payer at all teaches nothing: there is no key to
  // recognise it by next time.
  if (!key) return null;

  try {
    const existing = unwrap<{ id: string; times_set_aside: number }[]>(
      await supabase
        .from("deposit_rules")
        .select("id,times_set_aside")
        .eq("counterparty", key)
        .limit(1),
    );

    const times = (existing[0]?.times_set_aside ?? 0) + 1;
    const patch = {
      label,
      times_set_aside: times,
      reason,
      last_set_aside_by: profileId,
      last_set_aside_at: new Date().toISOString(),
    };

    if (existing[0]) {
      await updateRow("deposit_rules", existing[0].id, patch);
    } else {
      await insertRow("deposit_rules", { counterparty: key, ...patch });
    }

    return learningNotice({
      counterpartyLabel: label,
      timesSetAsideNow: times,
    });
  } catch {
    return null;
  }
}

/** Every payer the list has learned about, for the automatic pass and Tools. */
export async function fetchDepositRules(): Promise<
  (DepositRule & { id: string; label: string })[]
> {
  const rows = unwrap<
    {
      id: string;
      counterparty: string;
      label: string;
      times_set_aside: number;
      reason: string;
    }[]
  >(
    await supabase
      .from("deposit_rules")
      .select("id,counterparty,label,times_set_aside,reason")
      .order("times_set_aside", { ascending: false })
      .limit(200),
  );
  return rows.map((row) => ({
    id: row.id,
    counterparty: row.counterparty,
    label: row.label,
    timesSetAside: Number(row.times_set_aside),
    reason: row.reason,
  }));
}

/** Forget a payer, so the list starts asking about them again. */
export async function forgetDepositRule(id: string): Promise<void> {
  await supabase.from("deposit_rules").delete().eq("id", id);
}

/**
 * Put a deposit the software set aside back in front of a person.
 *
 * And un-learn the step that put it there. This undo only ever appears on a
 * deposit a *rule* set aside, so pressing it is somebody disagreeing with the
 * rule — and a rule that survives being disagreed with sets the next payment
 * from that payer aside as well, with the only cure buried in Tools. See
 * `timesAfterUndo`.
 *
 * The rule is not deleted: the count comes down by one, so a payer set aside
 * many times is still a habit and one that had only just been learned starts
 * asking again. A failure to find the rule is not a failure to put the deposit
 * back, so it is swallowed — the money is the part that matters.
 */
export async function undoSetAside(
  depositId: string,
  counterparty?: string | null,
): Promise<void> {
  await updateRow("mercury_deposits", depositId, {
    state: "new",
    dismissed_reason: null,
    set_aside_automatically: false,
  });

  const key = normaliseCounterparty(counterparty);
  if (!key) return;
  try {
    const existing = unwrap<{ id: string; times_set_aside: number }[]>(
      await supabase
        .from("deposit_rules")
        .select("id,times_set_aside")
        .eq("counterparty", key)
        .limit(1),
    );
    const rule = existing[0];
    if (!rule) return;
    await updateRow("deposit_rules", rule.id, {
      times_set_aside: timesAfterUndo(rule.times_set_aside),
    });
  } catch {
    // The deposit is back, which is what the button promised.
  }
}

/** Deposits a learned rule set aside, newest first, for review. */
export async function fetchSetAsideAutomatically(): Promise<
  MercuryDepositRow[]
> {
  return unwrap<MercuryDepositRow[]>(
    await supabase
      .from("mercury_deposits")
      .select("*")
      .eq("state", "dismissed")
      .eq("set_aside_automatically", true)
      .order("posted_at", { ascending: false, nullsFirst: false })
      .limit(25),
  );
}

/**
 * The paid payment requests whose money nobody has seen arrive.
 *
 * `state = 'paid'` means Mercury told us the customer paid. `mercury_deposit_id
 * IS NULL` means no bank line has been tied to it yet — the rows this whole
 * feature is about. Ordered newest first and capped, because a request that
 * has been waiting months for its money is a bookkeeping question, not a
 * matching one.
 */
export async function fetchPaidRequestsAwaitingMoney(): Promise<PaidRequest[]> {
  const rows = unwrap<
    {
      id: string;
      job_id: string;
      /** Null on a link raised by hand (ADR 0141). */
      slot: number | null;
      is_manual: boolean | null;
      /** Which link-by-hand this is on the job, for `1051-M1` (ADR 0141). */
      manual_number: number | null;
      purpose: string | null;
      mercury_invoice_id: string | null;
      amount_cents: number | null;
      amount_paid_cents: number | null;
      paid_at: string | null;
      job: {
        job_number: number | null;
        contact: { full_name: string | null; email: string | null } | null;
      } | null;
    }[]
  >(
    await supabase
      .from("mercury_payment_requests")
      .select(
        "id,job_id,slot,is_manual,manual_number,purpose,mercury_invoice_id," +
          "amount_cents,amount_paid_cents,paid_at," +
          " job:jobs(job_number, contact:contacts(full_name,email))",
      )
      .eq("state", "paid")
      .is("mercury_deposit_id", null)
      .order("paid_at", { ascending: false, nullsFirst: false })
      .limit(100),
  );
  if (rows.length === 0) return [];

  /*
   * A payment whose money somebody already accounted for is not waiting.
   *
   * `mercury_deposit_id` is only ever written by the two automatic paths here.
   * Every other way a payment gets settled — "Matches a payment", Find the
   * job, a split, the QuickBooks close, a check clearing — leaves it null, and
   * nothing backfills it. So a request settled last week went on being offered
   * as the owner of any credit that happened to be the same size:
   *
   *   Cat's $9,000 is confirmed by a person on the 3rd. A stranger's $9,000
   *   arrives on the 5th, and Money In offers it as "the invoice Cat Edwards
   *   paid" — one click files somebody else's money under Cat's job.
   *
   * Josh found that on 2026-09-13. The question is not "has this column been
   * written" but "has this payment's money been seen", and three tables can
   * answer it.
   */
  const jobIds = [...new Set(rows.map((row) => row.job_id))];
  const [deposits, quickbooks, handed] = await Promise.all([
    supabase
      .from("mercury_deposits")
      .select("job_id,slots")
      .in("job_id", jobIds)
      .eq("state", "confirmed"),
    supabase
      .from("quickbooks_payments")
      .select("job_id,slots")
      .in("job_id", jobIds)
      .eq("state", "applied"),
    supabase
      .from("customer_payments")
      .select("job_id,slots,mercury_payment_request_id")
      .in("job_id", jobIds)
      .eq("state", "paid"),
  ]);

  /*
   * Each settled slot, with the link a record named, if any. Pressing "It's
   * in the bank" on a link records it against the link's own slot (ADR 0312),
   * and that record is the money the link's credit will bring, not other money
   * that settled the slot: the link stays waiting, so its credit still ties to
   * it and its card fee is still booked. `null` is anything that settled the
   * slot without naming a link.
   */
  const settled = new Map<string, Set<string | null>>();
  for (const source of [deposits, quickbooks, handed]) {
    for (const row of unwrap<
      {
        job_id: string | null;
        slots: number[] | null;
        mercury_payment_request_id?: string | null;
      }[]
    >(source)) {
      if (!row.job_id) continue;
      for (const slot of row.slots ?? []) {
        const key = `${row.job_id}:${Number(slot)}`;
        const by = settled.get(key) ?? new Set<string | null>();
        by.add(row.mercury_payment_request_id ?? null);
        settled.set(key, by);
      }
    }
  }
  const settledByOtherMoney = (row: (typeof rows)[number]) =>
    [...(settled.get(`${row.job_id}:${Number(row.slot)}`) ?? [])].some(
      (linkId) => linkId !== row.id,
    );

  return (
    rows
      // A manual link has no slot, so nothing can have settled "its slot" —
      // its money is its own (ADR 0141).
      .filter((row) => row.slot == null || !settledByOtherMoney(row))
      .map((row) => ({
        id: row.id,
        jobId: row.job_id,
        slot: row.slot == null ? null : (row.slot as PaymentSlot),
        // The rule `buildInvoice` stamps, imported rather than repeated: a second
        // copy of it here is a copy that drifts.
        invoiceNumber: invoiceNumberFor(row.job?.job_number, row.slot, {
          manualReference: row.manual_number,
        }),
        mercuryInvoiceId: row.mercury_invoice_id,
        amountCents: row.amount_cents,
        amountPaidCents: row.amount_paid_cents,
        paidAt: row.paid_at,
        customerName: row.job?.contact?.full_name ?? null,
        // The email Mercury billed, which is how a card charge inside a Stripe
        // payout finds its way back to a job (ADR 0138).
        customerEmail: row.job?.contact?.email ?? null,
      }))
  );
}

/**
 * What the customer was asked for on this request.
 *
 * Passed as `chargeCents` whenever a bank line is confirmed against a payment
 * request, so the processing fee is measured against **what that invoice
 * billed** rather than against the slot's scheduled amount. Josh, 2026-09-13:
 * where the two differ — an estimate changed after the link went out, or a
 * $2,000 request sitting on a $9,000 deposit slot — one click ticked the whole
 * slot and booked the difference as a "Card processing fee".
 *
 * Null when nobody knows, which leaves the old behaviour: the slot's amount.
 */
function askedFor(request: PaidRequest): Cents | null {
  const asked = request.amountPaidCents ?? request.amountCents;
  return asked != null && asked > 0 ? cents(asked) : null;
}

/**
 * Give a claim back, where this caller is still the one holding it.
 *
 * `eq("mercury_deposit_id", deposit.id)` matters: by the time a failure is
 * being cleaned up somebody else may legitimately hold the claim, and wiping
 * theirs would be worse than leaving ours.
 */
async function releaseClaim(
  requestId: string,
  depositId: string,
): Promise<void> {
  try {
    await supabase
      .from("mercury_payment_requests")
      .update({ mercury_deposit_id: null, mercury_transaction_id: null })
      .eq("id", requestId)
      .eq("mercury_deposit_id", depositId);
  } catch {
    // Nothing to do about a failure here: the request keeps a claim on a
    // deposit nobody confirmed, which a person can undo on Money In.
  }
}

/** A deposit row as packages/core reads it. */
function bankLineOf(deposit: MercuryDepositRow): BankLine {
  return {
    id: deposit.id,
    amountCents: Number(deposit.amount_cents),
    counterparty: deposit.counterparty,
    description: deposit.description,
    externalMemo: deposit.external_memo ?? null,
    note: deposit.note ?? null,
    trackingNumber: deposit.tracking_number ?? null,
    raw: (deposit.raw as Record<string, unknown> | null) ?? null,
    postedAt: deposit.posted_at,
  };
}

/**
 * Tie a request to its bank line, once.
 *
 * `is("mercury_deposit_id", null)` is the whole safety of this: two polls
 * racing, or a person confirming while a poll runs, and only one of them
 * writes. The other is told nothing was claimed and leaves the deposit alone,
 * rather than confirming the same money against the job a second time.
 */
async function claimRequestForDeposit(
  requestId: string,
  deposit: MercuryDepositRow,
): Promise<boolean> {
  const claimed = unwrap<{ id: string }[]>(
    await supabase
      .from("mercury_payment_requests")
      .update({
        mercury_deposit_id: deposit.id,
        mercury_transaction_id: deposit.mercury_transaction_id,
      })
      .eq("id", requestId)
      .is("mercury_deposit_id", null)
      .select("id"),
  );
  return claimed.length > 0;
}

/**
 * What Money In should say about each deposit, from Mercury's own side.
 *
 * Returned for the panel to render: a `suggested` answer becomes one button
 * with a sentence over it, and a person still presses it. `linked` answers are
 * not here — `linkBankLinesToRequests` below has already confirmed those and
 * they are off the list.
 */
export async function fetchMercurySuggestions(
  deposits: readonly MercuryDepositRow[],
): Promise<Map<string, DepositLink>> {
  if (deposits.length === 0) return new Map();
  const requests = await fetchPaidRequestsAwaitingMoney();
  if (requests.length === 0) return new Map();
  return linkBankLines(deposits.map(bankLineOf), requests);
}

/**
 * A person saying yes to Mercury's suggestion.
 *
 * The same confirmation any deposit gets — `source: "person"`, because a
 * person pressed it — with the link written first, so the request stops being
 * offered against any other bank line whatever happens next.
 */
export async function confirmDepositForRequest(
  deposit: MercuryDepositRow,
  request: PaidRequest,
  profileId: string | null,
): Promise<{ ticked: string[]; why: string }> {
  /*
   * The claim decides whether this confirmation happens at all.
   *
   * It used to be written and its answer thrown away, so two people pressing
   * the button — or one person on a stale tab — confirmed the same money
   * twice, ticking the box twice and booking the processing fee twice. The
   * claim is a conditional update on a row that still has no bank line, so
   * exactly one caller can win it; losing it means somebody else already
   * accounted for this money (Josh, 2026-09-13).
   */
  if (!(await claimRequestForDeposit(request.id, deposit))) {
    throw new Error(
      "Somebody has already matched this payment — reload the list to see where it went.",
    );
  }
  try {
    return await confirmDeposit(deposit, request.jobId, profileId, "person", {
      slot: request.slot,
      chargeCents: askedFor(request),
      because: "request",
    });
  } catch (e) {
    // The claim goes back, or this payment can never be matched again.
    await releaseClaim(request.id, deposit.id);
    throw e;
  }
}

/**
 * Every bank line that names the payment request it settles, confirmed.
 *
 * Runs on the poll, before anybody looks at the list. Only `linked` answers
 * act — an identifier on the line that names exactly one request Mercury says
 * was paid. A `suggested` answer is left for the person, and a `none` answer
 * leaves the deposit exactly where it was (ADR 0142).
 *
 * The deciding is `linkBankLines` in packages/core, where it is tested.
 */
async function linkBankLinesToRequests(
  /** Anything that went wrong on one line, for the board to show. */
  problems: string[],
): Promise<number> {
  const deposits = await fetchOpenDeposits();
  if (deposits.length === 0) return 0;
  const requests = await fetchPaidRequestsAwaitingMoney();
  if (requests.length === 0) return 0;

  const answers = linkBankLines(deposits.map(bankLineOf), requests);
  let linked = 0;
  for (const deposit of deposits) {
    const answer = answers.get(deposit.id);
    if (answer?.kind !== "linked") continue;
    /*
     * The first payment and the final payment settle themselves. A second or
     * third draw does not.
     *
     * Jonathon, 2026-09-12: "if there are values in the 2nd and 3rd box i want
     * those to be manually marked paid since those are very rare." The line
     * stays on Money In, where the identifier still names the customer, and a
     * person presses the button (ADR 0139).
     */
    if (!settlesWithoutAPerson(answer.request.slot)) continue;
    // Claim first, confirm second. A claim that loses the race means somebody
    // else has this money; confirming anyway would tick a box twice.
    if (!(await claimRequestForDeposit(answer.request.id, deposit))) continue;
    try {
      await confirmDeposit(deposit, answer.request.jobId, null, "mercury", {
        // Null on a manual link, which tells `confirmDeposit` to work out from
        // the amount what this settles rather than being told.
        slot: answer.request.slot,
        chargeCents: askedFor(answer.request),
        because: "request",
      });
      linked += 1;
    } catch (e) {
      /*
       * The job has no invoice amount, or the write failed.
       *
       * The claim goes back. It was written a moment ago to stop two callers
       * confirming the same money; holding it after the confirmation failed
       * would point the request at a deposit nobody confirmed, and the
       * suggestion would never come back (Josh, 2026-09-13).
       */
      await releaseClaim(answer.request.id, deposit.id);
      // The link stands —
      // it is true, and it is what stops this being offered again — and the
      // deposit stays on the list for a person. Nothing is lost, but it is
      // said out loud rather than swallowed: a deposit that quietly did not
      // confirm is the kind of thing nobody finds for a month.
      problems.push(
        `matching ${formatMoney(cents(Number(deposit.amount_cents)))} to a payment request: ` +
          (e instanceof Error ? e.message : String(e)),
      );
    }
  }
  return linked;
}

/** A charge inside a Stripe payout, as `stripe_payout_charges` holds it. */
interface PayoutChargeRow {
  id: string;
  stripe_payout_id: string;
  stripe_charge_id: string;
  payout_amount_cents: number | null;
  payout_arrival_date: string | null;
  gross_cents: number;
  net_cents: number;
  email: string | null;
  charged_at: string | null;
  /** The deposit this charge's money already sits on, if it was carved out. */
  deposit_id: string | null;
}

/**
 * Card charges Stripe has told us about and nobody has settled.
 *
 * Capped at two hundred, which is more card payments than this company has
 * taken in its life. A charge whose payout never turned up in the bank stays
 * here, and is reconsidered on every poll — that is the whole point: the money
 * usually arrives two or three days after the charge does.
 */
async function fetchUnsettledCharges(): Promise<PayoutChargeRow[]> {
  return unwrap<PayoutChargeRow[]>(
    await supabase
      .from("stripe_payout_charges")
      .select(
        "id,stripe_payout_id,stripe_charge_id,payout_amount_cents," +
          "payout_arrival_date,gross_cents,net_cents,email,charged_at," +
          "deposit_id",
      )
      .is("settled_at", null)
      .order("payout_arrival_date", { ascending: false, nullsFirst: false })
      .limit(200),
  );
}

const chargeOf = (
  row: PayoutChargeRow,
): PayoutCharge & {
  payoutAmountCents: number | null;
  payoutArrivalDate: string | null;
} => ({
  id: row.id,
  stripePayoutId: row.stripe_payout_id,
  stripeChargeId: row.stripe_charge_id,
  grossCents: Number(row.gross_cents),
  netCents: Number(row.net_cents),
  email: row.email,
  chargedAt: row.charged_at,
  payoutAmountCents:
    row.payout_amount_cents == null ? null : Number(row.payout_amount_cents),
  payoutArrivalDate: row.payout_arrival_date,
});

/**
 * Charges whose money is already on a deposit of its own, finished off.
 *
 * Only the last step is left for these: claim the payment request and confirm.
 * Nothing is split and no bank line is looked for, because the line they came
 * out of is already carved up.
 */
async function finishCarvedCharges(
  rows: readonly PayoutChargeRow[],
): Promise<number> {
  if (rows.length === 0) return 0;
  const deposits = unwrap<MercuryDepositRow[]>(
    await supabase
      .from("mercury_deposits")
      .select("*")
      .in(
        "id",
        rows.map((row) => row.deposit_id).filter((id): id is string => !!id),
      ),
  );
  const byId = new Map(deposits.map((deposit) => [deposit.id, deposit]));
  const requests = await fetchPaidRequestsAwaitingMoney();

  let settled = 0;
  for (const row of rows) {
    const deposit = row.deposit_id ? byId.get(row.deposit_id) : null;
    // Gone, or already confirmed by a person while this was waiting. Either
    // way it is not ours to finish.
    if (!deposit || deposit.state !== "new") continue;
    const charge = chargeOf(row);
    const { settle } = chargesToRequests([charge], requests);
    const match = settle[0];
    if (!match) continue;
    /*
     * A second or third draw is confirmed by a person, here as everywhere.
     *
     * The payout path checks this before it carves a part; this path picks up
     * every carved part on the next poll and used to claim and confirm it
     * without asking, which walked straight past the rule (Josh, 2026-09-13).
     */
    if (!settlesWithoutAPerson(match.request.slot)) continue;
    if (await settleChargeOn(deposit, charge, match.request)) settled += 1;
  }
  return settled;
}

/**
 * What card money is waiting on, in a sentence a person can act on.
 *
 * Nothing read `unmatched_reason` until now: a payout with a refund in it, or
 * one whose credit has not landed, sat whole on Money In with no hint why, and
 * the only sign anything had been attempted was a count of the ones that
 * worked (Josh, 2026-09-13). Grouped by reason, because five charges out of
 * one payout all say the same thing.
 */
export async function fetchCardPayoutProblems(): Promise<string[]> {
  const rows = unwrap<{ unmatched_reason: string | null }[]>(
    await supabase
      .from("stripe_payout_charges")
      .select("unmatched_reason")
      .is("settled_at", null)
      .not("unmatched_reason", "is", null)
      .limit(200),
  );
  const seen = new Map<string, number>();
  for (const row of rows) {
    const why = (row.unmatched_reason ?? "").trim();
    if (why === "") continue;
    seen.set(why, (seen.get(why) ?? 0) + 1);
  }
  return [...seen.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([why, count]) => (count > 1 ? `${why} (${count} charges)` : why));
}

/** Say on a charge why it is still sitting there. Never fatal. */
async function noteOnCharge(id: string, why: string | null): Promise<void> {
  await supabase
    .from("stripe_payout_charges")
    .update({ unmatched_reason: why })
    .eq("id", id);
}

/**
 * Card payouts, taken apart and put against the jobs that paid them.
 *
 * A card does not arrive as itself: Stripe sweeps two or three days of them
 * into one credit, net of fees, with no customer name on it. This is what
 * turns that credit back into customers (ADR 0138):
 *
 *   1. find the bank line that is this payout — its amount to the cent, on or
 *      about the day Stripe promised;
 *   2. check Stripe's charges account for the whole of it, or leave the line
 *      alone: a split that does not add up is money recorded against a job
 *      that never arrived;
 *   3. carve the line into one part per charge, through the same
 *      `split_mercury_deposit` a person's split goes through;
 *   4. confirm the parts whose charge names a paid payment request, by the
 *      email Mercury billed. A part that names none stays on Money In for a
 *      person, for the right amount.
 *
 * A payout of one charge skips step 3: the line already is that charge, and
 * `split_mercury_deposit` refuses a split of one part, so sending it there
 * left the commonest card payout on Money In forever. The line itself is
 * claimed and confirmed exactly as a part would be.
 *
 * Every decision is in packages/core. This does the fetching and the writing.
 * Exported for its tests; `pollPaymentSources` is the only caller.
 */
export async function settleStripePayouts(): Promise<number> {
  const rows = await fetchUnsettledCharges();
  if (rows.length === 0) return 0;

  /*
   * A charge already carved out of its bank line is retried, not re-split.
   *
   * `deposit_id` says this charge has its own deposit row: the payout was
   * split and this part failed the claim, or the tab closed between the split
   * and the confirm. Sending it back through the payout path would look for a
   * bank line that is no longer open — the parent is `split` now — and
   * overwrite the real reason with "No credit of $X has arrived yet" on every
   * poll for ever (Josh, 2026-09-13). So they are finished on their own.
   */
  const carved = rows.filter((row) => row.deposit_id);
  let settled = await finishCarvedCharges(carved);

  const fresh = rows.filter((row) => !row.deposit_id);
  if (fresh.length === 0) return settled;

  const payouts = payoutsFromCharges(fresh.map(chargeOf));
  const deposits = await fetchOpenDeposits();
  const lines = deposits.map(bankLineOf);
  const byId = new Map(deposits.map((deposit) => [deposit.id, deposit]));
  const rowByCharge = new Map(fresh.map((row) => [row.id, row]));

  for (const payout of payouts) {
    const found = payoutOnTheStatement(payout, lines);
    if (found.kind !== "one") {
      for (const charge of payout.charges) {
        await noteOnCharge(charge.id, found.why);
      }
      continue;
    }
    const parent = byId.get(found.line.id);
    if (!parent) continue;

    const adds = chargesAccountForTheLine(payout, found.line);
    if (!adds.ready) {
      for (const charge of payout.charges)
        await noteOnCharge(charge.id, adds.why);
      continue;
    }

    // Who each charge belongs to, before anything is written. A payout where
    // nobody is recognised is left whole: splitting it would turn one line a
    // person can read into five they cannot.
    const requests = await fetchPaidRequestsAwaitingMoney();
    const { settle, leftovers } = chargesToRequests(payout.charges, requests);
    if (settle.length === 0) {
      for (const { charge, why } of leftovers)
        await noteOnCharge(charge.id, why);
      continue;
    }

    // One charge, and it was recognised: the bank line is that customer's
    // payment, whole. The nets were checked against it above, to the cent.
    if (!payoutNeedsSplitting(payout)) {
      const only = settle[0]!;
      if (await settleChargeOn(parent, only.charge, only.request)) settled += 1;
      continue;
    }

    const { data, error } = await supabase.rpc("split_mercury_deposit", {
      p_parent: parent.id,
      p_parts: payout.charges.map((charge) => ({
        net_cents: charge.netCents,
        gross_cents: charge.grossCents,
      })),
    });
    if (error) {
      for (const charge of payout.charges) {
        await noteOnCharge(charge.id, error.message);
      }
      continue;
    }

    // The parts come back in the order they went in, which is the order of
    // `payout.charges`.
    const parts = (data ?? []) as MercuryDepositRow[];
    if (parts.length !== payout.charges.length) continue;

    for (const [index, charge] of payout.charges.entries()) {
      const part = parts[index]!;
      const match = settle.find((one) => one.charge.id === charge.id);
      const row = rowByCharge.get(charge.id);
      if (!match) {
        // Somebody else's money, or a customer we cannot name. It is its own
        // deposit now, for the right amount, waiting on Money In.
        await supabase
          .from("stripe_payout_charges")
          .update({
            deposit_id: part.id,
            unmatched_reason:
              leftovers.find((one) => one.charge.id === charge.id)?.why ??
              "Nothing named this charge.",
          })
          .eq("id", row?.id ?? charge.id);
        continue;
      }

      // A middle draw is a person's to confirm, like any other (ADR 0139).
      // The part exists, for the right amount, and waits on Money In.
      if (!settlesWithoutAPerson(match.request.slot)) {
        await supabase
          .from("stripe_payout_charges")
          .update({
            deposit_id: part.id,
            unmatched_reason:
              "A second or third payment is confirmed by a person.",
          })
          .eq("id", row?.id ?? charge.id);
        continue;
      }

      if (await settleChargeOn(part, charge, match.request)) settled += 1;
    }
  }

  return settled;
}

/**
 * One charge settled against its payment request, on the deposit that holds
 * its money — a part carved out of the payout, or the whole bank line when the
 * charge was the only one in it. True when it was.
 *
 * Claim first, confirm second, as `linkBankLinesToRequests` does: a claim that
 * loses the race means somebody else has this money, and confirming anyway
 * would tick a box twice.
 */
async function settleChargeOn(
  deposit: MercuryDepositRow,
  charge: PayoutCharge,
  request: PaidRequest,
): Promise<boolean> {
  if (!(await claimRequestForDeposit(request.id, deposit))) return false;
  try {
    await confirmDeposit(deposit, request.jobId, null, "stripe", {
      slot: request.slot,
      // What the customer was charged, gross. Without it the fee would be
      // measured against the whole slot and a card payment would be booked
      // as a colossal processing fee.
      chargeCents: cents(charge.grossCents),
    });
    await supabase
      .from("stripe_payout_charges")
      .update({
        deposit_id: deposit.id,
        request_id: request.id,
        settled_at: new Date().toISOString(),
        unmatched_reason: null,
      })
      .eq("id", charge.id);
    return true;
  } catch (e) {
    // The claim goes back: a request pointing at a deposit nobody confirmed
    // is a payment that can never be matched again (#231's round 2).
    await releaseClaim(request.id, deposit.id);
    await noteOnCharge(charge.id, e instanceof Error ? e.message : String(e));
    return false;
  }
}

/** Deposits nobody has dealt with, newest first. */
export async function fetchOpenDeposits(): Promise<MercuryDepositRow[]> {
  return unwrap<MercuryDepositRow[]>(
    await supabase
      .from("mercury_deposits")
      .select("*")
      .eq("state", "new")
      .order("posted_at", { ascending: false, nullsFirst: false })
      .limit(50),
  );
}

/** Everything recorded against one job, whatever state it reached. */
export async function fetchJobPayments(jobId: string): Promise<{
  quickbooks: QuickbooksPaymentRow[];
  deposits: MercuryDepositRow[];
}> {
  const [quickbooks, deposits] = await Promise.all([
    supabase
      .from("quickbooks_payments")
      .select("*")
      .eq("job_id", jobId)
      .order("seen_at", { ascending: false }),
    supabase
      .from("mercury_deposits")
      .select("*")
      .eq("job_id", jobId)
      .order("posted_at", { ascending: false, nullsFirst: false }),
  ]);

  return {
    quickbooks: unwrap<QuickbooksPaymentRow[]>(quickbooks),
    deposits: unwrap<MercuryDepositRow[]>(deposits),
  };
}

/** A job a deposit might belong to, with how good a fit it is. */
export interface DepositSuggestion {
  jobId: string;
  label: string;
  /** Lower-cased haystack for the search box: number, name and address. */
  search: string;
  distance: number;
  match: PaymentMatch;
  slots: PaymentSlot[];
  /**
   * The payments this job actually has, for the screen to offer.
   *
   * Two on a job sold in halves; four on one sold in draws. Carried on the
   * suggestion rather than looked up later because the screen has the
   * suggestions and not the contexts, and a person choosing "Second payment"
   * on a job that has no second payment is a choice that means nothing.
   */
  payments: { slot: PaymentSlot; label: string; amount: Cents }[];
  /**
   * The middle draw this amount settles, when it is one.
   *
   * A draw has no checklist box, so `match` will never name it — but it is
   * still a payment this customer was asked for, and the button can say which
   * one rather than offering "Record it anyway".
   */
  middleFit: { slot: PaymentSlot; label: string } | null;
}

/**
 * Which jobs a deposit could be, closest first.
 *
 * Only jobs that have been sold and are still waiting for money are offered —
 * a job with both boxes ticked is not waiting for anything, and offering it
 * would be inviting the mistake this whole confirm step exists to prevent.
 */
/**
 * Every signed job's payment context, in a fixed number of queries.
 *
 * `fetchJobPaymentContext` costs six round trips for one job, which is fine
 * when a person opens one row and ruinous when the screen wants a best guess on
 * every card: thirty signed jobs against six deposits is a thousand queries.
 * This is the same arithmetic, batched — six queries whatever the size of the
 * board — and it is what lets the panel show each deposit's likely job without
 * anybody clicking first.
 */
export async function fetchPaymentContexts(): Promise<
  Map<string, JobPaymentContext & { label: string; search: string }>
> {
  const jobs = unwrap<
    {
      id: string;
      job_number: number | null;
      address: string | null;
      contact: { full_name: string | null } | null;
    }[]
  >(
    await supabase
      .from("jobs")
      .select("id,job_number,address, contact:contacts(full_name)")
      .not("signed_contract_at", "is", null)
      .neq("is_test", true)
      .order("signed_contract_at", { ascending: false })
      .limit(400),
  );
  const out = new Map<
    string,
    JobPaymentContext & { label: string; search: string }
  >();
  if (jobs.length === 0) return out;

  const ids = jobs.map((job) => job.id);
  const [invoices, signed, schedule, checklist, surcharges, signedBases] =
    await Promise.all([
      supabase
        .from("quickbooks_invoices")
        .select("job_id,amount_cents")
        .in("job_id", ids),
      // The fallback for a job nobody has pressed Sends on. Ordered so the newest
      // acceptance per job wins: a job presented and signed twice keeps both.
      supabase
        .from("proposal_acceptances")
        .select("total,accepted_at, proposals!inner(job_id)")
        .in("proposals.job_id", ids)
        .order("accepted_at", { ascending: false }),
      // Every slot the rep typed, not only the first. The middle two decide
      // what the FINAL payment is — it is the contract less all three — so
      // reading slot 1 alone made the final payment on a four-draw job read
      // too big, and no final payment on such a job ever matched.
      supabase
        .from("estimate_payments")
        .select("slot, amount, estimates!inner(job_id, is_active)")
        .in("estimates.job_id", ids)
        .eq("estimates.is_active", true)
        .in("slot", [1, 2, 3]),
      supabase
        .from("job_checklist_items")
        .select("id,job_id,label,status")
        .in("job_id", ids)
        .eq("phase", "production"),
      supabase
        .from("mercury_payment_requests")
        .select("job_id,slot,surcharge_cents")
        .in("job_id", ids)
        .eq("customer_method", "card")
        .in("state", ["sent", "paid"]),
      fetchSignedContractBases(ids),
    ]);

  const invoiced = new Map<string, number>();
  for (const row of unwrap<{ job_id: string; amount_cents: number | null }[]>(
    invoices,
  )) {
    if (row.amount_cents != null) invoiced.set(row.job_id, row.amount_cents);
  }

  // `proposal_acceptances.total` is a decimal of DOLLARS, where
  // `quickbooks_invoices.amount_cents` two blocks up is already cents. Getting
  // these the wrong way round is a hundredfold error in a number that decides
  // whether a customer still owes money.
  const signedTotal = new Map<string, Cents>();
  for (const row of unwrap<
    { total: NumericFromDb; proposals: { job_id: string } | null }[]
  >(signed)) {
    const jobId = row.proposals?.job_id;
    if (!jobId || signedTotal.has(jobId) || row.total == null) continue;
    signedTotal.set(jobId, toCents(row.total));
  }

  const firstTyped = new Map<string, Cents>();
  const middleTyped = new Map<string, Record<number, Cents>>();
  for (const row of unwrap<
    {
      slot: number | string;
      amount: NumericFromDb;
      estimates: { job_id: string } | null;
    }[]
  >(schedule)) {
    const jobId = row.estimates?.job_id;
    if (!jobId || row.amount == null || row.amount === "") continue;
    const slot = Number(row.slot);
    // `estimate_payments.amount` is a decimal of DOLLARS. Getting that the
    // wrong way round is a hundredfold error in a number that ticks a box.
    const amount = toCents(row.amount);
    if (slot === 1) {
      firstTyped.set(jobId, amount);
      continue;
    }
    // A typed zero is a real answer — "nothing at this stage" — and it is not
    // a payment, so it is not carried and the slot is simply not offered.
    if (amount <= 0) continue;
    const map = middleTyped.get(jobId) ?? {};
    map[slot] = amount;
    middleTyped.set(jobId, map);
  }

  const itemsByJob = new Map<
    string,
    Map<string, { id: string; status: string }>
  >();
  for (const row of unwrap<
    { id: string; job_id: string; label: string; status: string }[]
  >(checklist)) {
    const map = itemsByJob.get(row.job_id) ?? new Map();
    map.set(row.label, { id: row.id, status: row.status });
    itemsByJob.set(row.job_id, map);
  }

  const feesByJob = new Map<string, Record<number, Cents>>();
  for (const row of unwrap<
    { job_id: string; slot: number | string; surcharge_cents: NumericFromDb }[]
  >(surcharges)) {
    const fee = Math.round(Number(row.surcharge_cents ?? 0));
    if (fee <= 0) continue;
    const map = feesByJob.get(row.job_id) ?? {};
    map[Number(row.slot)] = cents(fee);
    feesByJob.set(row.job_id, map);
  }

  for (const job of jobs) {
    const contract = invoiced.get(job.id) ?? signedTotal.get(job.id) ?? 0;
    if (contract <= 0) continue;

    const items = itemsByJob.get(job.id) ?? new Map();
    // Nothing left to collect. Jonathon: "those customers will go away if
    // they're paid in full."
    if (items.get("Final Check")?.status === "done") continue;

    const name = job.contact?.full_name ?? "Unnamed";
    out.set(job.id, {
      jobId: job.id,
      contractCents: cents(contract),
      // What they signed, which is not what they are invoiced: the invoice
      // moves with a signed change order (ADR 0195) and the deposit must not
      // (ADR 0205).
      signedContractCents:
        signedBases.get(job.id) ?? signedTotal.get(job.id) ?? null,
      firstCents: firstTyped.get(job.id) ?? null,
      middleCents: middleTyped.get(job.id) ?? {},
      firstCollected: items.get("First Check")?.status === "done",
      surchargeBySlot: feesByJob.get(job.id) ?? {},
      items,
      label: `${job.job_number ? `#${job.job_number} · ` : ""}${name}${
        job.address ? ` · ${job.address}` : ""
      }`,
      search: [job.job_number, name, job.address].filter(Boolean).join(" "),
    });
  }
  return out;
}

/**
 * Which jobs a deposit could be, closest first.
 *
 * Pure ranking over contexts the panel already loaded — no queries of its own,
 * which is what makes it cheap enough to run for every deposit on screen rather
 * than only the one somebody opened.
 */
export function suggestJobsForDeposit(
  deposit: MercuryDepositRow,
  contexts: Map<string, JobPaymentContext & { label: string; search: string }>,
): DepositSuggestion[] {
  const suggestions: DepositSuggestion[] = [];
  for (const context of contexts.values()) {
    const match = matchOnJob(deposit.amount_cents, context, {
      netOfFees: true,
    });
    // Null means the amount is nothing like anything this job expects. It is
    // still offered — a customer paying an odd amount is exactly the case a
    // person is needed for — but it sorts to the bottom.
    const distance =
      suggestionDistance(cents(deposit.amount_cents), {
        contractCents: context.contractCents,
        firstCents: context.firstCents,
        middleCents: context.middleCents,
        firstCollected: context.firstCollected,
      }) ?? Number.MAX_SAFE_INTEGER;

    suggestions.push({
      jobId: context.jobId,
      label: context.label,
      search: context.search,
      distance,
      match,
      slots: match.slots,
      payments: paymentsOnOffer(context),
      middleFit: middlePaymentFit(cents(deposit.amount_cents), context, {
        netOfFees: true,
      }),
    });
  }
  return suggestions.sort((a, b) => a.distance - b.distance);
}

/**
 * Every amount some job is still waiting for.
 *
 * The safety valve behind the automatic set-aside: a familiar payer is not
 * enough on its own, and money that looks like a payment somebody owes always
 * goes to a person. Batched into three queries rather than the per-job walk
 * `suggestJobsForDeposit` does, because this runs over every new deposit on
 * every poll and that walk is a round trip per job.
 */
/** One page. Supabase's `db-max-rows`, which no `.limit()` can lift. */
const AWAITED_PAGE = 1000;

/** Enough pages for many years of invoices, stated rather than implied. */
const AWAITED_LIMIT = 20000;

/**
 * Every row of a query, walked past the server's page.
 *
 * `.limit(5000)` reads like a safeguard and is not one: Supabase applies
 * `db-max-rows` to every request and answers with the first thousand, no error
 * and no sign anything is missing — see `fetchCalls` in queries.ts, where
 * asking for five thousand returned one thousand. `.range()` is what walks
 * past it.
 */
async function everyRow<T>(
  page: (
    from: number,
    to: number,
  ) => PromiseLike<{
    data: unknown;
    error: { message: string } | null;
  }>,
): Promise<T[]> {
  const all: T[] = [];
  for (;;) {
    const got = unwrap<T[]>(
      await page(all.length, all.length + AWAITED_PAGE - 1),
    );
    all.push(...got);
    if (got.length < AWAITED_PAGE || all.length >= AWAITED_LIMIT) return all;
  }
}

/** Exported for `amounts-awaited.test.ts`, which pins that it reads whole. */
export async function fetchAmountsAwaited(): Promise<Cents[]> {
  // Read whole, a page at a time, and the invoices matter most.
  //
  // This list is the safety valve: a familiar payer is set aside
  // automatically *unless* the amount is one some job is waiting for. An
  // amount missing from here is a valve that fails open — and what goes
  // missing is not random, it is every job past the server's thousandth row.
  // A real customer's payment, from a payer somebody once set aside by
  // mistake, would then be dismissed by itself, with a reason like "Mercury
  // interest, not a customer" written against it, and nobody would chase the
  // money. That is the single outcome ADR 0098 says the valve exists to
  // prevent, and `quickbooks_invoices` gains a row for every job ever
  // invoiced, so it reaches a thousand on its own.
  const [invoices, schedule, checklist] = await Promise.all([
    everyRow<{ job_id: string; amount_cents: number | null }>((from, to) =>
      supabase
        .from("quickbooks_invoices")
        .select("job_id,amount_cents")
        .not("amount_cents", "is", null)
        .order("job_id")
        .range(from, to),
    ),
    everyRow<{
      amount: number | string | null;
      estimates: { job_id: string } | null;
    }>((from, to) =>
      supabase
        .from("estimate_payments")
        .select("amount, estimates!inner(job_id, is_active)")
        .eq("estimates.is_active", true)
        .eq("slot", 1)
        .order("id")
        .range(from, to),
    ),
    everyRow<{ job_id: string; label: string; status: string }>((from, to) =>
      supabase
        .from("job_checklist_items")
        .select("job_id,label,status")
        .in("label", ["First Check", "Final Check"])
        .order("job_id")
        .range(from, to),
    ),
  ]);

  const first = new Map<string, Cents>();
  for (const row of schedule) {
    const jobId = row.estimates?.job_id;
    if (jobId && row.amount != null && row.amount !== "") {
      first.set(jobId, toCents(row.amount));
    }
  }

  const done = new Set<string>();
  for (const row of checklist) {
    if (row.status === "done") done.add(`${row.job_id}:${row.label}`);
  }

  const awaited: Cents[] = [];
  for (const invoice of invoices) {
    const contract = cents(Number(invoice.amount_cents ?? 0));
    if (contract <= 0) continue;
    // A job with both boxes ticked is waiting for nothing, and offering its
    // amounts here would hold up a deposit for a job that is already settled.
    if (done.has(`${invoice.job_id}:Final Check`)) continue;

    const firstCollected = done.has(`${invoice.job_id}:First Check`);
    // No `signedContractCents` here, deliberately. This is a list of amounts
    // worth recognising across every job in the company, and the signatures
    // are one more full read to get. On a job with a signed change order the
    // real Final is larger than this (ADR 0205), so that payment simply is not
    // recognised automatically and a person matches it — which is the safe way
    // round for a rule that decides whether to set money aside by itself.
    const expected = expectedPayments({
      contractCents: contract,
      firstCents: first.get(invoice.job_id) ?? null,
      firstCollected,
    });
    awaited.push(contract, expected.final);
    if (!firstCollected) awaited.push(expected.first);
  }
  return awaited;
}

/**
 * Set aside the deposits a learned rule recognises.
 *
 * Runs over deposits nobody has looked at yet, which on a normal day is none or
 * one. Returns what it did so the screen can say so — a list that quietly
 * shortens itself is a list people stop trusting.
 */
async function setAsideWhatIsRecognised(): Promise<number> {
  const open = await fetchOpenDeposits();
  if (open.length === 0) return 0;

  const [rules, awaited] = await Promise.all([
    fetchDepositRules(),
    fetchAmountsAwaited(),
  ]);
  const earned = rules.filter(
    (rule) => rule.timesSetAside >= RULE_EARNED_AFTER,
  );
  if (earned.length === 0) return 0;

  let set = 0;
  for (const deposit of open) {
    const verdict = setAsideVerdict({
      counterparty: deposit.counterparty,
      amount: cents(deposit.amount_cents),
      rules: earned,
      awaited,
    });
    if (!verdict.setAside) continue;

    await updateRow("mercury_deposits", deposit.id, {
      state: "dismissed",
      dismissed_reason: verdict.why,
      set_aside_automatically: true,
    });
    set += 1;
  }
  return set;
}

/**
 * Close the deposits QuickBooks has already identified.
 *
 * Jonathon, 2026-09-06: "if kelsey assigns it to a customer before lysa does i
 * want it to automatically update and remove it from the list."
 *
 * This is the one place a deposit is matched to a job without somebody pressing
 * the button, and it is safe for a specific reason. A bank line is not proof of
 * whose money it is — that is why the confirm step exists — but a QuickBooks
 * payment carries the invoice id, `quickbooks_invoices` maps that to the job,
 * and a person recorded it there against a named customer. The identification
 * has been made; it was simply made in the other system.
 *
 * Only ever against a job QuickBooks has already applied a payment to, and only
 * when the amount still matches. `confirmDeposit` recomputes the match, so a
 * deposit that no longer fits is left for a person with no harm done.
 */
async function closeDepositsQuickbooksHasIdentified(): Promise<number> {
  const open = await fetchOpenDeposits();
  if (open.length === 0) return 0;

  // Payments a person matched to a customer in QuickBooks, newest first.
  const applied = unwrap<{ job_id: string }[]>(
    await supabase
      .from("quickbooks_payments")
      .select("job_id")
      .eq("state", "applied")
      .order("applied_at", { ascending: false })
      .limit(200),
  );
  const jobIds = [...new Set(applied.map((row) => row.job_id))];
  if (jobIds.length === 0) return 0;

  let closed = 0;
  for (const deposit of open) {
    for (const jobId of jobIds) {
      /*
       * The match is checked here rather than left to `confirmDeposit` to
       * refuse. It used to throw on an amount that fits nothing, and this loop
       * leaned on that — then `confirmDeposit` learned to record an odd amount
       * anyway, so leaning on it would have attached every unrecognised deposit
       * to whichever job QuickBooks happened to have touched most recently.
       *
       * A person may say "this is theirs" about an amount that fits nothing.
       * A loop may not.
       */
      const context = await fetchJobPaymentContext(jobId);
      if (!context) continue;
      if (context.items.get("Final Check")?.status === "done") continue;
      if (
        !matchOnJob(deposit.amount_cents, context, { netOfFees: true }).matched
      ) {
        continue;
      }

      try {
        await confirmDeposit(deposit, jobId, null, "quickbooks");
        closed += 1;
        break;
      } catch {
        // Something else went wrong on this job. The deposit stays where it is
        // and a person decides, exactly as before.
      }
    }
  }
  return closed;
}

/**
 * Tell whoever chases money that some has arrived.
 *
 * Once per deposit, stamped on the row, so two people opening the board does
 * not send it twice. Only about money that still needs a person: a deposit a
 * rule set aside is not news, and one QuickBooks already identified is
 * announced as settled rather than as something to do.
 */
async function announceNewMoney(): Promise<void> {
  const settings = unwrap<{ money_notify_profile_id: string | null }[]>(
    await supabase
      .from("company_settings")
      .select("money_notify_profile_id")
      .eq("id", 1)
      .limit(1),
  );
  const profileId = settings[0]?.money_notify_profile_id;
  if (!profileId) return;

  const waiting = unwrap<MercuryDepositRow[]>(
    await supabase
      .from("mercury_deposits")
      .select("*")
      .eq("state", "new")
      .is("notified_at", null)
      .order("posted_at", { ascending: false, nullsFirst: false })
      .limit(10),
  );
  if (waiting.length === 0) return;

  const total = waiting.reduce((sum, row) => sum + Number(row.amount_cents), 0);
  const body =
    waiting.length === 1
      ? `${formatMoney(cents(waiting[0]!.amount_cents))} from ${
          waiting[0]!.counterparty?.trim() || "an unnamed payer"
        }. Say whose it is on the Ongoing Jobs board.`
      : `${waiting.length} deposits totalling ${formatMoney(cents(total))} are waiting to be matched.`;

  try {
    await supabase.functions.invoke("send-push", {
      body: {
        profileIds: [profileId],
        title: "Money in",
        body,
        url: "/#view=Ongoing",
      },
    });
    // Stamped whatever the push did. A notification that failed is not worth
    // repeating on every board open for the rest of the month.
  } finally {
    await supabase
      .from("mercury_deposits")
      .update({ notified_at: new Date().toISOString() })
      .in(
        "id",
        waiting.map((row) => row.id),
      );
  }
}

// ------------------------------------------- money handed over in person ---
//
// Jonathon, 2026-09-10: a check or cash payment should get the customer a
// receipt "when a rep clicks on payment submitted or when the deposit is
// initiated in mercury. whatever comes first." Cash is paid when it is
// recorded; a check is submitted until it is in the bank. ADR 0125.

/** A check or cash payment, as `customer_payments` holds it. */
export interface CustomerPaymentRow {
  id: string;
  job_id: string;
  method: CustomerPaymentMethod;
  amount_cents: number;
  check_number: string | null;
  /** A date column: a string, never read with new Date. */
  received_on: string;
  slots: number[];
  state: CustomerPaymentState;
  paid_at: string | null;
  source: "person" | "mercury";
  mercury_transaction_id: string | null;
  mercury_deposit_id: string | null;
  receipt_number: string;
  receipt_sent_at: string | null;
  receipt_sent_to: string | null;
  receipt_error: string | null;
  receipt_filed_at: string | null;
  /** A monthly payment toward the job's in-house payment plan (ADR 0286). */
  toward_plan?: boolean;
  /** The payment link this is the money for, said in the bank by a person (ADR 0312). */
  mercury_payment_request_id?: string | null;
  created_at: string;
}

/** Today, as an ISO date in the reader's own timezone. */
function today(): string {
  return localDay(new Date().toISOString());
}

/** The calendar day a moment fell on, here. A moment, so `new Date` is right. */
function localDay(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return today();
  return new Date(at.getTime() - at.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
}

const errorText = (e: unknown): string =>
  e instanceof Error ? e.message : String(e);

export async function fetchCustomerPayments(
  jobId: string,
): Promise<CustomerPaymentRow[]> {
  return unwrap<CustomerPaymentRow[]>(
    await supabase
      .from("customer_payments")
      .select("*")
      .eq("job_id", jobId)
      .order("created_at"),
  );
}

export interface RecordPaymentInput {
  jobId: string;
  method: CustomerPaymentMethod;
  amountCents: Cents;
  checkNumber?: string | null;
  /** The day the money was handed over, as an ISO date. */
  receivedOn: string;
  /**
   * Which scheduled payment a person said this is. Undefined works it out
   * from the amount; null means it is none of them.
   */
  slot?: PaymentSlot | null;
  /** A cleared box was already ticked when its record-payment offer opened. */
  clearedByBoxSlot?: PaymentSlot | undefined;
  profileId: string | null;
  source?: "person" | "mercury";
  mercuryTransactionId?: string | null;
  /** A monthly payment toward the job's in-house payment plan (ADR 0286). Names no slot. */
  towardPlan?: boolean;
}

/**
 * Write down money somebody was handed, and tick what it ticks.
 *
 * Cash ticks Submitted and the cleared box together; a check only Submitted
 * (`labelsOnRecording`). Either way the Mercury request for the same payment
 * is called off, so the customer who has just paid is not sent a reminder the
 * next morning asking them to.
 */
/** Whether the job's active estimate carries an in-house payment plan (ADR 0286). */
async function jobIsOnPlan(jobId: string): Promise<boolean> {
  const rows = unwrap<{ plan_monthly_cents?: number | null }[]>(
    await supabase
      .from("estimates")
      .select("plan_monthly_cents")
      .eq("job_id", jobId)
      .eq("is_active", true),
  );
  return (
    Array.isArray(rows) &&
    rows.some((row) => Number(row?.plan_monthly_cents ?? 0) > 0)
  );
}

export async function recordCustomerPayment(
  input: RecordPaymentInput,
): Promise<{ payment: CustomerPaymentRow; ticked: string[]; why: string }> {
  const context = await fetchJobPaymentContext(input.jobId);
  const amount = cents(input.amountCents);
  if (amount <= 0) throw new Error("A payment needs an amount.");

  // On an in-house payment plan the Final is paid month by month and never
  // as one payment, whichever path records it (ADR 0286).
  const onPlan = await jobIsOnPlan(input.jobId);
  if (input.slot != null && !mayPaySlot(input.slot, onPlan)) {
    throw new Error(PLAN_FINAL_REFUSED);
  }

  const slotName = (slot: PaymentSlot) =>
    (PAYMENT_SLOT_LABEL[slot] ?? `payment ${slot}`).toLowerCase();
  const worked = input.towardPlan
    ? {
        match: {
          matched: false,
          slots: [],
          why: "Recorded toward the payment plan.",
        } as PaymentMatch,
        towardPlan: true,
      }
    : input.slot === null
      ? {
          match: {
            matched: false,
            slots: [],
            why: "Recorded as none of the scheduled payments, so no payment box was ticked.",
          } as PaymentMatch,
          towardPlan: false,
        }
      : input.slot !== undefined
        ? {
            match: {
              matched: true,
              slots: [input.slot],
              why: `Recorded as the ${slotName(input.slot)}.`,
            } as PaymentMatch,
            towardPlan: false,
          }
        : // Worked out from the amount — a check deposited before anybody
          // recorded it, most often. On a plan, what is not the down payment
          // or a draw is a monthly payment toward the plan.
          settledOnPlan(
            context
              ? matchOnJob(amount, context)
              : {
                  matched: false,
                  slots: [],
                  why: "This job has no contract amount yet, so it settles no scheduled payment.",
                },
            onPlan,
          );
  const match = worked.match;
  const towardPlan = worked.towardPlan;

  // The offer from an already-ticked cleared box is a person's statement that
  // this check is in the bank. Re-read that box now: it may have been unticked
  // or the person may have selected another payment while the form was open.
  const clearedLabel = input.clearedByBoxSlot
    ? PAYMENT_CHECKLIST_LABELS[input.clearedByBoxSlot]?.cleared
    : null;
  const clearedByTickedBox = recordedFromClearedOffer({
    method: input.method,
    offeredSlot: input.clearedByBoxSlot,
    recordedSlots: match.slots,
    clearedBoxStatus: clearedLabel
      ? context?.items.get(clearedLabel)?.status
      : null,
  });
  const state = stateOnRecording(input.method, clearedByTickedBox);
  const checkNumber =
    input.method === "check" ? (input.checkNumber ?? "").trim() || null : null;

  const payment = await insertRow<CustomerPaymentRow>("customer_payments", {
    job_id: input.jobId,
    method: input.method,
    amount_cents: amount,
    check_number: checkNumber,
    received_on: input.receivedOn,
    slots: match.slots,
    state,
    paid_at: state === "paid" ? new Date().toISOString() : null,
    source: input.source ?? "person",
    mercury_transaction_id: input.mercuryTransactionId ?? null,
    toward_plan: towardPlan,
    created_by: input.profileId,
  });

  const what =
    `${CUSTOMER_PAYMENT_METHOD_LABEL[input.method]} of ${formatMoney(amount)}` +
    (checkNumber ? ` (check no. ${checkNumber})` : "") +
    (input.source === "mercury" ? " deposited in Mercury" : " recorded");
  const ticked = context
    ? await tick(
        context,
        labelsOnRecording(input.method, match),
        `${what}: ${match.why}`,
      )
    : [];
  if (ticked.length === 0) {
    await insertRow("activities", {
      job_id: input.jobId,
      type: "note",
      direction: "internal",
      body: `${what} against this job. ${match.why}`,
    });
  }

  await stopAskingFor(input.jobId, match.slots, input.method);
  return { payment, ticked, why: match.why };
}

/**
 * Call off the Mercury requests for payments that have just been made.
 *
 * Through the function's `cancel` action — the requests table is admin-only to
 * write, and cancelling is what that action exists for ("a rep who has taken a
 * cheque"). A failure leaves a reminder that should not go, which a rep can
 * still stop by hand; it does not un-record the money.
 */
async function stopAskingFor(
  jobId: string,
  slots: readonly number[],
  method: CustomerPaymentMethod,
): Promise<void> {
  if (slots.length === 0) return;
  const open = unwrap<{ id: string }[]>(
    await supabase
      .from("mercury_payment_requests")
      .select("id")
      .eq("job_id", jobId)
      .in("slot", slots as number[])
      .in("state", ["pending", "sent", "failed"]),
  );
  for (const row of open) {
    try {
      await supabase.functions.invoke("mercury", {
        body: {
          action: "cancel",
          requestId: row.id,
          reason: `Paid by ${method} — recorded in Roof Board.`,
        },
      });
    } catch {
      // See above.
    }
  }
}

/**
 * A rep says the customer is paying by check, or takes it back (ADR 0263).
 *
 * On the job, because that is what the rep means — "they're mailing a check",
 * not "slot 1 is being paid by check" — and because the reminder sweep reads
 * the job to decide whether anything on it is chased. Said on the job's
 * history too, so whoever picks the job up next knows why the customer has
 * not been written to. Recording the check clears it (a database trigger, so
 * every door that records one does).
 */
export async function sayCheckIsComing(input: {
  jobId: string;
  profileId: string | null;
  coming: boolean;
}): Promise<{
  check_expected_at: string | null;
  check_expected_by: string | null;
}> {
  const patch = input.coming
    ? {
        check_expected_at: new Date().toISOString(),
        check_expected_by: input.profileId,
      }
    : { check_expected_at: null, check_expected_by: null };
  await updateRow("jobs", input.jobId, patch);
  await insertRow("activities", {
    job_id: input.jobId,
    type: "note",
    direction: "internal",
    body: input.coming
      ? `Customer is paying by check. Payment reminders wait ${CHECK_IN_THE_MAIL_DAYS} days for it.`
      : "The check is not coming after all. Payment reminders are back on.",
  });
  return patch;
}

/**
 * A person turns the job's automatic payment reminders off, or back on
 * (ADR 0302).
 *
 * On the job, like the check above, and with no expiry: off until somebody
 * turns them on. The payment requests and their links are left alone, so the
 * customer can still pay and a rep can still send a letter by hand. Said on
 * the job's history, so whoever picks the job up knows why nobody is chased.
 */
export async function turnPaymentRemindersOff(input: {
  jobId: string;
  profileId: string | null;
  off: boolean;
}): Promise<{
  payment_reminders_off_at: string | null;
  payment_reminders_off_by: string | null;
}> {
  const patch = input.off
    ? {
        payment_reminders_off_at: new Date().toISOString(),
        payment_reminders_off_by: input.profileId,
      }
    : { payment_reminders_off_at: null, payment_reminders_off_by: null };
  await updateRow("jobs", input.jobId, patch);
  await insertRow("activities", {
    job_id: input.jobId,
    type: "note",
    direction: "internal",
    body: input.off
      ? "Payment reminders turned off. The customer is not emailed about a payment until they are turned back on."
      : "Payment reminders turned back on.",
  });
  return patch;
}

/**
 * Draw the receipt, email it, and file it on the job.
 *
 * Once. `receipt_sent_at` is stamped before anything is drawn, only where it
 * was empty, so the two doors — a rep recording the check, Mercury seeing it
 * deposited — cannot both send one. A send that fails puts it back and says
 * why on the payment, where Resend is.
 *
 * From the rep's Gmail, so the customer hears from the person they dealt
 * with; from whoever approved it when the rep has not connected theirs, and
 * then the letter names the rep rather than pretending to be them
 * (`receiptSender`, ADR 0140).
 */
/**
 * Whether the rep has a Gmail account connected, which decides whose mailbox
 * a receipt goes from (`receiptSender`, ADR 0140). Every signed-in user may
 * read `gmail_accounts`; nothing in it is secret.
 */
async function repHasGmail(repId: string | null): Promise<boolean> {
  if (!repId) return false;
  const rows = unwrap<{ profile_id: string }[]>(
    await supabase
      .from("gmail_accounts")
      .select("profile_id")
      .eq("profile_id", repId)
      .limit(1),
  );
  return rows.length > 0;
}

/**
 * Put a receipt in front of a person rather than sending it.
 *
 * Jonathon, 2026-09-12, on the receipt a check or cash payment gets: "this
 * email needs to be a prompt we can review before sending it." A rep standing
 * with the customer gets that prompt straight away; a receipt the software
 * worked out by itself — a check deposit clearing at the bank, matched to the
 * one customer owed exactly that amount — has nobody standing there, so it is
 * stamped as waiting and turns up on the rep's list (ADR 0140).
 *
 * Stamped once, and never over a receipt that has already gone.
 */
export async function queueReceiptForReview(paymentId: string): Promise<void> {
  const { error } = await supabase
    .from("customer_payments")
    .update({ receipt_waiting_since: new Date().toISOString() })
    .eq("id", paymentId)
    .is("receipt_sent_at", null)
    .is("receipt_waiting_since", null)
    // A link's money: Mercury receipted it (ADR 0312).
    .is("mercury_payment_request_id", null);
  // A stamp that fails silently is a receipt nobody is ever asked to send.
  if (error) throw new Error(error.message);
}

/**
 * The letter a receipt would go out as, drawn but not sent and not stamped.
 *
 * What the review prompt shows. Everything here is read the same way
 * `sendCustomerPaymentReceipt` reads it, so what somebody approves is what
 * leaves — the only difference is that this claims nothing and writes nothing.
 */
export async function draftCustomerPaymentReceipt(
  paymentId: string,
  /** Who is about to read it, which decides whose voice it is written in. */
  profileId: string | null,
): Promise<{
  to: string | null;
  subject: string;
  text: string;
  receiptNumber: string;
  amountCents: number;
  customerName: string | null;
  /** What the customer still owes after this payment, for the prompt to show. */
  balanceAfter: string | null;
  /** What this payment settles, in words. */
  settles: string | null;
  /**
   * The PDF, drawn now and sent with whatever the reviewer approves.
   *
   * It used to be drawn again at send time, so a payment recorded in between
   * changed the attachment while the reviewer's eyes were on the old one
   * (Josh, 2026-09-13). The document a person approves is the document that
   * goes.
   */
  document: { filename: string; content: string };
}> {
  const rows = unwrap<CustomerPaymentRow[]>(
    await supabase.from("customer_payments").select("*").eq("id", paymentId),
  );
  const payment = rows[0];
  if (!payment) throw new Error("That payment could not be found.");
  // Mercury emailed the customer the receipt for a link payment (ADR 0312).
  if (!mayReceipt(payment)) throw new Error(RECEIPT_REFUSED_FOR_LINK);

  const job = await fetchJob(payment.job_id);
  if (!job) throw new Error("The job could not be found.");

  const [settings, balance, repRow] = await Promise.all([
    fetchCompanySettings(),
    fetchJobBalanceInput(job),
    job.rep_id
      ? supabase
          .from("profiles")
          .select("full_name,phone,email")
          .eq("id", job.rep_id)
          .maybeSingle()
      : Promise.resolve({ data: null }),
  ]);
  const rep = (repRow.data ?? null) as {
    full_name: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  const contact = job.contact ?? null;

  const receipt = buildReceipt({
    number: payment.receipt_number,
    receivedOn: payment.received_on,
    company: {
      name: settings?.name,
      address: settings?.address,
      phone: settings?.phone,
      website: settings?.website,
    },
    customer: contact ?? {},
    jobNumber: job.job_number,
    jobAddress: job,
    method: payment.method,
    checkNumber: payment.check_number,
    amount: cents(payment.amount_cents),
    slots: payment.slots as PaymentSlot[],
    towardPlan: payment.toward_plan === true,
    balance,
    rep,
  });

  /*
   * Whose voice it is written in: the owner of the mailbox it will leave
   * from, worked out here exactly as the send works it out.
   *
   * Jonathon, 2026-09-19: "The rep's Gmail, falling back to the approver's
   * mailbox when the rep hasn't connected theirs." So an office admin reading
   * Mike's receipt reads Mike's letter when it is going from Mike's Gmail, and
   * a letter that names Mike when it is going from the office — never Mike's
   * signature out of somebody else's mailbox (Josh, 2026-09-13).
   */
  const { fromRep } = receiptSender({
    repProfileId: job.rep_id ?? null,
    approverProfileId: profileId,
    repHasGmail: await repHasGmail(job.rep_id ?? null),
  });
  const letter = receiptEmail(receipt, {
    firstName: contact?.first_name,
    fromRep,
  });

  return {
    to: (contact?.email ?? "").trim() || null,
    subject: letter.subject,
    text: letter.text,
    receiptNumber: payment.receipt_number,
    amountCents: Number(payment.amount_cents),
    customerName: contact?.full_name ?? null,
    // What the PDF says, so the person approving the letter sees the figures
    // on the document rather than only the covering note.
    balanceAfter:
      receipt.balanceAfter == null
        ? null
        : formatMoney(cents(Number(receipt.balanceAfter))),
    settles: receipt.description,
    document: {
      filename: receiptFilename(receipt.number),
      content: await receiptPdfBase64(receipt),
    },
  };
}

export async function sendCustomerPaymentReceipt(
  paymentId: string,
  profileId: string | null,
  /**
   * `email: false` draws and files the receipt without sending it — for a
   * payment somebody already receipted by hand. The payment then says so, and
   * the automatic door leaves it alone.
   *
   * `wording` is what a person read and approved in the review prompt, sent
   * verbatim. Jonathon, 2026-09-12: "this email needs to be a prompt we can
   * review before sending it" (ADR 0140). Without it the letter is drawn from
   * the template, which is what the resend button does.
   */
  options: {
    email?: boolean;
    wording?: { subject: string; text: string };
    /** The PDF a person read in the prompt, sent rather than drawn again. */
    document?: { filename: string; content: string } | undefined;
  } = {},
): Promise<{ sent: boolean; to: string | null; why: string | null }> {
  const now = new Date().toISOString();
  const claimed = unwrap<CustomerPaymentRow[]>(
    await supabase
      .from("customer_payments")
      .update({ receipt_sent_at: now, receipt_error: null })
      .eq("id", paymentId)
      .is("receipt_sent_at", null)
      // Never a link's money: Mercury receipted it (ADR 0312). In the claim,
      // so no send gets past it, whatever screen it came from.
      .is("mercury_payment_request_id", null)
      .select("*"),
  );
  const payment = claimed[0];
  if (!payment) {
    const rows = unwrap<
      Pick<CustomerPaymentRow, "mercury_payment_request_id">[]
    >(
      await supabase
        .from("customer_payments")
        .select("mercury_payment_request_id")
        .eq("id", paymentId),
    );
    if (rows[0] && !mayReceipt(rows[0])) {
      return { sent: false, to: null, why: RECEIPT_REFUSED_FOR_LINK };
    }
    return { sent: false, to: null, why: "The receipt has already been sent." };
  }

  try {
    const job = await fetchJob(payment.job_id);
    if (!job) throw new Error("The job could not be found.");

    const [settings, balance, repRow] = await Promise.all([
      fetchCompanySettings(),
      fetchJobBalanceInput(job),
      job.rep_id
        ? supabase
            .from("profiles")
            .select("full_name,phone,email")
            .eq("id", job.rep_id)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    const rep = (repRow.data ?? null) as {
      full_name: string | null;
      phone: string | null;
      email: string | null;
    } | null;
    const contact = job.contact ?? null;

    const receipt = buildReceipt({
      number: payment.receipt_number,
      receivedOn: payment.received_on,
      company: {
        name: settings?.name,
        address: settings?.address,
        phone: settings?.phone,
        website: settings?.website,
      },
      customer: contact ?? {},
      jobNumber: job.job_number,
      jobAddress: job,
      method: payment.method,
      checkNumber: payment.check_number,
      amount: cents(payment.amount_cents),
      slots: payment.slots as PaymentSlot[],
      towardPlan: payment.toward_plan === true,
      balance,
      rep,
    });
    // The document the reviewer read, where there was one. Drawing it again
    // here would attach a different receipt from the one they approved if any
    // other payment landed in between (Josh, 2026-09-13).
    const content =
      options.document?.content ?? (await receiptPdfBase64(receipt));
    const filename =
      options.document?.filename ?? receiptFilename(receipt.number);
    const attachments = [{ filename, mimeType: "application/pdf", content }];

    const to = (contact?.email ?? "").trim();
    let sentTo: string | null = null;
    let problem: string | null = null;
    if (options.email === false) {
      problem = "Recorded without emailing it.";
    } else if (!to) {
      problem = "There is no email address on file for this customer.";
    } else {
      try {
        /*
         * Jonathon, 2026-09-19: "The rep's Gmail, falling back to the
         * approver's mailbox when the rep hasn't connected theirs." (ADR
         * 0140.) Decided up front from the connection, not by trying the
         * rep's and catching: a send that fails for any other reason is a
         * failure to report, not a reason to move the letter into another
         * mailbox.
         */
        const sender = receiptSender({
          repProfileId: job.rep_id ?? null,
          approverProfileId: profileId,
          repHasGmail: await repHasGmail(job.rep_id ?? null),
        });
        const letter =
          options.wording ??
          receiptEmail(receipt, {
            firstName: contact?.first_name,
            fromRep: sender.fromRep,
          });
        await sendEmail({
          to,
          subject: letter.subject,
          text: letter.text,
          jobId: job.id,
          contactId: job.contact_id ?? null,
          fromProfileId: sender.fromProfileId,
          attachments,
        });
        sentTo = to;
      } catch (e) {
        problem = errorText(e);
      }
    }

    // On the Docs card, sent or not — the receipt exists either way.
    let filedAt = payment.receipt_filed_at;
    if (!filedAt) {
      try {
        await fileEmailedPdf({
          jobId: job.id,
          kind: "receipt",
          title: `Receipt ${receipt.number}`,
          filename,
          base64: content,
          emailedTo: sentTo ?? "",
          profileId,
        });
        filedAt = new Date().toISOString();
      } catch {
        // Filing is not sending. The customer's copy is what matters.
      }
    }

    await updateRow("customer_payments", payment.id, {
      receipt_sent_at: sentTo ? now : null,
      receipt_sent_to: sentTo,
      receipt_error: problem,
      receipt_filed_at: filedAt,
      // Off the list of receipts waiting to be read, whether it went or not:
      // a failure leaves `receipt_error` on the row, which is what the Resend
      // button reads, and an unsendable receipt should not sit in somebody's
      // to-do list for ever (ADR 0140).
      receipt_waiting_since: null,
    });
    return { sent: Boolean(sentTo), to: sentTo, why: problem };
  } catch (e) {
    await updateRow("customer_payments", payment.id, {
      receipt_sent_at: null,
      receipt_error: errorText(e),
      // Off the list, like any other send that failed (ADR 0140). The error is
      // what the Resend button reads, and a receipt that cannot be drawn at
      // all would otherwise sit on somebody's to-do list for ever, asking to
      // be sent and failing every time.
      receipt_waiting_since: null,
    });
    return { sent: false, to: null, why: errorText(e) };
  }
}

/** The payment a Mercury transaction already became, if any. */
async function paymentForTransaction(
  transactionId: string,
): Promise<CustomerPaymentRow | null> {
  const rows = unwrap<CustomerPaymentRow[]>(
    await supabase
      .from("customer_payments")
      .select("*")
      .eq("mercury_transaction_id", transactionId)
      .limit(1),
  );
  return rows[0] ?? null;
}

/**
 * A check a rep recorded on this job that no deposit has claimed yet, for the
 * same amount — the deposit is that check, not a second one.
 */
async function checkAwaitingDeposit(
  jobId: string,
  amountCents: number,
): Promise<CustomerPaymentRow | null> {
  const rows = unwrap<CustomerPaymentRow[]>(
    await supabase
      .from("customer_payments")
      .select("*")
      .eq("job_id", jobId)
      .eq("method", "check")
      .eq("amount_cents", amountCents)
      .is("mercury_transaction_id", null)
      .order("created_at")
      .limit(1),
  );
  return rows[0] ?? null;
}

/** Tie a recorded check to the deposit Mercury says it became. */
async function claimTransaction(
  payment: CustomerPaymentRow,
  transactionId: string,
): Promise<CustomerPaymentRow> {
  const rows = unwrap<CustomerPaymentRow[]>(
    await supabase
      .from("customer_payments")
      .update({ mercury_transaction_id: transactionId })
      .eq("id", payment.id)
      .select("*"),
  );
  return rows[0] ?? { ...payment, mercury_transaction_id: transactionId };
}

/** The check is in the bank: paid, and its cleared box ticked. */
async function markCheckPaid(
  payment: CustomerPaymentRow,
  deposit: MercuryDepositRow,
): Promise<void> {
  const context = await fetchJobPaymentContext(payment.job_id);
  if (context && payment.slots.length > 0) {
    await tick(
      context,
      labelsOnClearing(payment.slots as PaymentSlot[]),
      `The check on receipt ${payment.receipt_number} cleared in Mercury`,
    );
  }
  await updateRow("customer_payments", payment.id, {
    state: "paid",
    paid_at: deposit.posted_at ?? new Date().toISOString(),
    mercury_deposit_id: deposit.id,
    mercury_transaction_id:
      payment.mercury_transaction_id ?? deposit.mercury_transaction_id,
  });
}

/**
 * A check already paid — by a person ticking its box — that the bank has now
 * been seen to hold.
 *
 * Josh, round 2 of #245: a check "First Check" or "Final Check" settled was
 * skipped here as already paid, so it never learnt its deposit, and unticking
 * the box later put money the bank really received back to owed. Tying it is
 * what makes the untick leave it alone (ADR 0159). `paid_at` is left as the
 * person said: it was already paid, and the date is theirs.
 *
 * Only a row not already tied to a deposit, so two confirmations cannot fight
 * over which one the check is.
 */
async function tieToDeposit(
  payment: CustomerPaymentRow,
  deposit: MercuryDepositRow,
): Promise<void> {
  unwrap(
    await supabase
      .from("customer_payments")
      .update({
        mercury_deposit_id: deposit.id,
        mercury_transaction_id:
          payment.mercury_transaction_id ?? deposit.mercury_transaction_id,
      })
      .eq("id", payment.id)
      .is("mercury_deposit_id", null)
      .select("id"),
  );
}

/**
 * Somebody confirmed a check deposit against a job on the Money In list.
 *
 * The check a rep recorded becomes paid; or, if nobody recorded one, it is
 * recorded now — already paid — and the customer gets their receipt.
 */
async function checkConfirmedByAPerson(
  deposit: MercuryDepositRow,
  jobId: string,
  profileId: string | null,
  options: { review?: boolean } = {},
): Promise<void> {
  let payment =
    (await paymentForTransaction(deposit.mercury_transaction_id)) ??
    (await checkAwaitingDeposit(jobId, deposit.amount_cents));
  if (payment && payment.job_id !== jobId) return;
  if (!payment) {
    ({ payment } = await recordCustomerPayment({
      jobId,
      method: "check",
      amountCents: cents(deposit.amount_cents),
      receivedOn: localDay(deposit.posted_at ?? new Date().toISOString()),
      profileId,
      mercuryTransactionId: deposit.mercury_transaction_id,
    }));
  }
  if (payment.state !== "paid") await markCheckPaid(payment, deposit);
  else if (!payment.mercury_deposit_id) await tieToDeposit(payment, deposit);
  if (!payment.receipt_sent_at && !payment.receipt_error) {
    if (options.review === false) {
      // The person matching the money said this customer needs no email. The
      // receipt is still drawn and filed on the job; what is skipped is the
      // customer's copy and the place on somebody's list asking for it.
      await sendCustomerPaymentReceipt(payment.id, profileId, {
        email: false,
      });
      return;
    }
    // Not sent: put in front of somebody (ADR 0140).
    await queueReceiptForReview(payment.id);
  }
}

/** A check deposit as the poll hands it back, pending or posted. */
export interface SeenCheck {
  mercuryTransactionId: string;
  amountCents: number;
  counterparty: string | null;
  description: string | null;
  kind?: string | null;
}

/**
 * Every job of every customer called this, with what each is still owed.
 *
 * Narrowed in the database by the payer's surname, then decided by
 * `sameName` in packages/core — which is exact, because the answer emails
 * somebody.
 */
async function checkCandidates(payer: string): Promise<CheckCandidate[]> {
  const words = payer.split(/[\s,]+/).filter(Boolean);
  const longest = [...words].sort((a, b) => b.length - a.length)[0];
  if (!longest) return [];
  const contacts = unwrap<{ id: string; full_name: string | null }[]>(
    await supabase
      .from("contacts")
      .select("id,full_name")
      .ilike("full_name", `%${longest.replace(/[%_\\]/g, "")}%`)
      .limit(50),
  ).filter((row) => sameName(row.full_name ?? "", payer));
  if (contacts.length === 0) return [];

  const jobs = unwrap<{ id: string; contact_id: string }[]>(
    await supabase
      .from("jobs")
      .select("id,contact_id")
      .in(
        "contact_id",
        contacts.map((row) => row.id),
      ),
  );

  const candidates: CheckCandidate[] = [];
  for (const row of jobs) {
    const job = await fetchJob(row.id);
    if (!job || job.is_test) continue;
    const balance = jobBalance(await fetchJobBalanceInput(job));
    candidates.push({
      jobId: job.id,
      customerName:
        contacts.find((contact) => contact.id === row.contact_id)?.full_name ??
        "",
      outstanding: balance.outstanding,
      due: balance.lines
        .filter((line) => !line.paid)
        .map((line) => line.amount),
    });
  }
  return candidates;
}

/**
 * A person saying a recorded check has landed in the bank.
 *
 * Ticking "First Check" or "Final Check" on the production checklist is
 * exactly that claim, and until 2026-09-14 it told the money side nothing.
 * Tu Lee's job 1032 read $1,100 owed with the box ticked and his check sitting
 * in `submitted` for three days, because the only thing that had ever settled
 * a check was seeing its deposit in Mercury. On a check that never shows up
 * there — or has not yet — the job stayed wrong and no screen offered a way to
 * say otherwise.
 *
 * What it does NOT do is tick the box; the person just did that. Nor does it
 * touch the receipt: that went when the check was recorded, and a second one
 * is not owed because the money finally cleared.
 *
 * Returns how many it settled, which is normally one and is often zero — most
 * ticks are on a job whose deposit already did the work.
 *
 * Each write is guarded on the row still being `submitted`: the list was read
 * a moment ago, and a deposit confirmed in between has already settled the
 * check with the bank's own date, which this must not overwrite.
 */
async function clearRecordedChecks(
  jobId: string,
  slot: PaymentSlot,
): Promise<number> {
  const rows = unwrap<CustomerPaymentRow[]>(
    await supabase.from("customer_payments").select("*").eq("job_id", jobId),
  );
  let settled = 0;
  for (const id of checksSettledByBox(slot, rows)) {
    const written = unwrap<{ id: string }[]>(
      await supabase
        .from("customer_payments")
        .update({ state: "paid", paid_at: new Date().toISOString() })
        .eq("id", id)
        .eq("state", "submitted")
        .select("id"),
    );
    settled += written.length;
  }
  return settled;
}

/**
 * The same person taking that back.
 *
 * Jonathon, 2026-09-14, on a box ticked by mistake: "check goes back to
 * unpaid." Unticking "First Check" or "Final Check" undoes exactly what
 * ticking it did — the check waits again, and the job owes what it owed.
 *
 * Only a check this box banked. One Mercury has seen deposited keeps its
 * money: `checksUnsettledByBox` leaves anything carrying a transaction or a
 * deposit alone. The receipt stays sent; it was always only about a check
 * being handed over.
 *
 * Returns how many it put back, usually zero, and how many it refused to
 * because the bank holds them (ADR 0159).
 *
 * Each write is guarded on the row still being `paid` and still tied to
 * nothing in Mercury. A deposit confirmed on Money In between the read and
 * the write ties the check, and money the bank received is never put back.
 */
async function unclearRecordedChecks(
  jobId: string,
  slot: PaymentSlot,
): Promise<{ putBack: number; banked: number }> {
  const [payments, landed] = await Promise.all([
    supabase.from("customer_payments").select("*").eq("job_id", jobId),
    linksTheBankHas(jobId),
  ]);
  const rows = unwrap<CustomerPaymentRow[]>(payments);
  const candidates = checksUnsettledByBox(slot, rows, landed);
  let putBack = 0;
  for (const id of candidates) {
    const written = unwrap<{ id: string }[]>(
      await supabase
        .from("customer_payments")
        .update({ state: "submitted", paid_at: null })
        .eq("id", id)
        .eq("state", "paid")
        .is("mercury_deposit_id", null)
        .is("mercury_transaction_id", null)
        .select("id"),
    );
    putBack += written.length;
  }
  // A candidate the guard refused was tied (or settled) by somebody else in
  // between, which is the bank holding it too.
  const refused = candidates.length - putBack;
  return {
    putBack,
    banked: checksTheBankHolds(slot, rows, landed).length + refused,
  };
}

/**
 * The job's payment links whose own credit the bank has confirmed. A link's
 * money said to be in the bank is held by the bank once it has (ADR 0312),
 * so unticking its box leaves it paid.
 */
async function linksTheBankHas(jobId: string): Promise<Set<string>> {
  const [links, credits] = await Promise.all([
    supabase
      .from("mercury_payment_requests")
      .select("id,mercury_deposit_id")
      .eq("job_id", jobId),
    supabase
      .from("mercury_deposits")
      .select("id")
      .eq("job_id", jobId)
      .eq("state", "confirmed"),
  ]);
  return linksWhoseCreditLanded(
    unwrap<{ id: string; mercury_deposit_id: string | null }[]>(links).map(
      (row) => ({ id: row.id, mercuryDepositId: row.mercury_deposit_id }),
    ),
    new Set(unwrap<{ id: string }[]>(credits).map((row) => row.id)),
  );
}

/**
 * Whether the job is on a payment plan, and whether its banked payments have
 * paid it off, for `mayTickClearedBox` (ADR 0286).
 *
 * Only Final Check asks, and only a plan job is worth the balance: every
 * other tick costs nothing here.
 */
async function planOnTheBox(
  jobId: string,
  slot: PaymentSlot,
): Promise<{ onPlan: boolean; paidOff: boolean }> {
  const none = { onPlan: false, paidOff: false };
  if (mayPaySlot(slot, true) || !(await jobIsOnPlan(jobId))) return none;
  const [plan, job] = await Promise.all([
    fetchJobPlans([jobId]).then((plans) => plans.get(jobId)),
    fetchJob(jobId),
  ]);
  if (!plan || !job) return { onPlan: true, paidOff: false };
  const balance = jobBalance(await fetchJobBalanceInput(job));
  // Banked money only: the box says the money is in the bank.
  const standing = jobPlanStanding(
    balance,
    plan.terms,
    planBalanceInput(plan).planPaidCents,
    new Date().toISOString().slice(0, 10),
  );
  return { onPlan: true, paidOff: standing?.paidOff === true };
}

/**
 * Mark a payment toward a plan as in the bank, by hand (ADR 0286).
 *
 * A plan payment names no slot, so no box settles it, and a check Mercury
 * never matches would otherwise stay on its way for good. Guarded on the row
 * still waiting, as a box's settle is, so a Money In confirmation in between
 * is not written over. Returns whether it changed anything.
 */
export async function markPlanPaymentBanked(
  payment: Pick<CustomerPaymentRow, "id" | "state" | "toward_plan">,
): Promise<boolean> {
  if (!mayMarkPlanPaymentBanked(payment)) return false;
  const written = unwrap<{ id: string }[]>(
    await supabase
      .from("customer_payments")
      .update({ state: "paid", paid_at: new Date().toISOString() })
      .eq("id", payment.id)
      .eq("state", "submitted")
      .eq("toward_plan", true)
      .select("id"),
  );
  return written.length > 0;
}

/** What a box change did to the recorded checks on its payment. */
export interface BoxMoneyOutcome {
  /** Checks that became paid because the box was ticked. */
  settled: number;
  /** Checks that went back to waiting because the box was unticked. */
  putBack: number;
  /** Checks an untick left paid, because Mercury has seen them. */
  banked: number;
}

/**
 * Tick, untick or skip a checklist box, and let the money follow it.
 *
 * The one way the web app changes a checklist box's status, so a box ticked
 * from anywhere on it means the same thing (CLAUDE.md rule 5).
 * `clearedBoxChange` in packages/core decides whether the change touches the
 * money at all; this does what it says.
 *
 * The box is written first and a failure there throws, so the caller can put
 * the box back. The money is second and its failure does not throw: the box
 * really did change, and saying so while the check stays as it was is truer
 * than pretending neither happened. `problem` carries why.
 *
 * `moneyFollows: false` writes the box alone. It is for a caller that has
 * already settled the one payment it means and ticks the box after it, as
 * It's in the bank does: running the settle path there would mark paid every
 * other payment waiting on that slot, a check Mercury has not seen included.
 */
export async function setChecklistItemStatus(
  item: Pick<JobChecklistItem, "id" | "job_id" | "label" | "status">,
  status: "pending" | "done" | "na",
  profileId: string | null,
  options: { moneyFollows?: boolean } = {},
): Promise<{ money: BoxMoneyOutcome; problem: string | null }> {
  const change = clearedBoxChange(item.label, item.status, status);
  // On a payment plan Final Check waits for the plan to be paid off, and is
  // refused before the box is written, so the checklist never says the Final
  // is in while the plan is still being paid (ADR 0286).
  if (
    change?.direction === "settle" &&
    !mayTickClearedBox(
      change.slot,
      await planOnTheBox(item.job_id, change.slot),
    )
  ) {
    throw new Error(PLAN_FINAL_CHECK_REFUSED);
  }

  await updateRow("job_checklist_items", item.id, {
    status,
    completed_at: status === "done" ? new Date().toISOString() : null,
    completed_by: status === "done" ? profileId : null,
  });

  const money: BoxMoneyOutcome = { settled: 0, putBack: 0, banked: 0 };
  if (options.moneyFollows === false) return { money, problem: null };
  if (!change) return { money, problem: null };
  try {
    if (change.direction === "settle") {
      money.settled = await clearRecordedChecks(item.job_id, change.slot);
    } else {
      Object.assign(
        money,
        await unclearRecordedChecks(item.job_id, change.slot),
      );
    }
    return { money, problem: null };
  } catch (e) {
    return { money, problem: errorText(e) };
  }
}

/**
 * Check deposits, recorded and settled without anybody pressing a button.
 *
 * The receipt is the one thing that is not done here any more. From
 * 2026-09-12 it is drafted and put in front of a person instead, because
 * Jonathon asked for a prompt he can read before a customer does (ADR 0140).
 *
 * Jonathon expects this door to be the first one, most of the time: the check
 * goes into Mercury before anyone thinks to record it. For each check the
 * poll saw —
 *
 *  - already tied to a payment: nothing to decide. Settle it if it has posted.
 *  - otherwise, whose is it? `identifyCheck` answers only when the name on the
 *    deposit is exactly one customer owed exactly this amount. If it cannot
 *    say, the check waits, and once it posts it is on the Money In list for a
 *    person like any other deposit.
 *  - a rep already recorded it: tie the two together. If not, record it now.
 *  - queue its receipt for somebody to read, if it has none; settle it, if it
 *    has posted.
 *
 * Each check is caught on its own. One that goes wrong is left for a person.
 */
export async function receiptsForCheckDeposits(
  pending: readonly SeenCheck[],
): Promise<{ receipted: number; settled: number }> {
  // `receipted` counts receipts now WAITING to be read, not sent (ADR 0140).
  const posted = (await fetchOpenDeposits()).filter((deposit) =>
    isCheckDeposit({ description: deposit.description }),
  );
  const seen: (SeenCheck & { deposit: MercuryDepositRow | null })[] = [
    ...pending.map((check) => ({ ...check, deposit: null })),
    ...posted.map((deposit) => ({
      mercuryTransactionId: deposit.mercury_transaction_id,
      amountCents: deposit.amount_cents,
      counterparty: deposit.counterparty,
      description: deposit.description,
      deposit,
    })),
  ];

  let receipted = 0;
  let settled = 0;
  for (const check of seen) {
    try {
      let payment = await paymentForTransaction(check.mercuryTransactionId);
      if (!payment) {
        const payer = checkPayerName(check);
        const identity = identifyCheck({
          payer,
          amountCents: cents(check.amountCents),
          candidates: payer ? await checkCandidates(payer) : [],
        });
        if (!identity.jobId) continue;

        const recorded = await checkAwaitingDeposit(
          identity.jobId,
          check.amountCents,
        );
        payment = recorded
          ? await claimTransaction(recorded, check.mercuryTransactionId)
          : (
              await recordCustomerPayment({
                jobId: identity.jobId,
                method: "check",
                amountCents: cents(check.amountCents),
                receivedOn: today(),
                profileId: null,
                source: "mercury",
                mercuryTransactionId: check.mercuryTransactionId,
              })
            ).payment;
      }

      if (!payment.receipt_sent_at && !payment.receipt_error) {
        // Nobody is standing here — this is a check the bank cleared and Roof
        // Board recognised — so the receipt waits to be read rather than going
        // out on its own (ADR 0140).
        await queueReceiptForReview(payment.id);
        receipted += 1;
      }

      if (check.deposit && payment.state !== "paid") {
        await confirmDeposit(check.deposit, payment.job_id, null, "check");
        await markCheckPaid(payment, check.deposit);
        settled += 1;
      }
    } catch {
      // Left for a person, on the Money In list once it posts.
    }
  }
  return { receipted, settled };
}

/**
 * Ask QuickBooks and Mercury what has happened, then apply what can be applied.
 *
 * Failures are swallowed into the returned note rather than thrown: this runs
 * when somebody opens a board, and an accounting service being down must not
 * stop the board from rendering. The polls are safe to repeat, so the next
 * person to open it tries again.
 */
export async function pollPaymentSources(): Promise<{
  applied: number;
  unmatched: number;
  /** Deposits QuickBooks had already identified, closed without a person. */
  closed: number;
  /** Bank lines that named the payment request they settle (ADR 0142). */
  linked: number;
  /** Card charges settled out of a Stripe payout (ADR 0138). */
  cards: number;
  /** Deposits a learned rule recognised and set aside. */
  setAside: number;
  problems: string[];
}> {
  const problems: string[] = [];
  // Checks deposited and still clearing, which the deposits poll hands back
  // rather than records (ADR 0125).
  let pendingChecks: SeenCheck[] = [];

  const ask = async (name: "quickbooks" | "mercury", action: string) => {
    try {
      const { data, error } = await supabase.functions.invoke(name, {
        body: { action },
      });
      const message =
        (data as { error?: string } | null)?.error ?? error?.message;
      if (message) problems.push(`${name}: ${message}`);
      return data as Record<string, unknown> | null;
    } catch (e) {
      problems.push(`${name}: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  };

  await Promise.all([
    ask("quickbooks", "payments"),
    // Has a payment request Roof Board raised been paid? A schedule asks this
    // every fifteen minutes now (docs/DEPLOYMENT.md), so this call is no
    // longer the only door — but the board opening is still the moment
    // somebody is about to look at the money, and asking again costs one
    // request per outstanding invoice.
    ask("mercury", "sync"),
    // What was inside each card payout. Recorded here, taken apart below.
    ask("mercury", "stripe"),
    ask("mercury", "deposits").then((data) => {
      if (Array.isArray(data?.pendingChecks)) {
        pendingChecks = data.pendingChecks as SeenCheck[];
      }
    }),
    // The every-other-day letters to customers who have not paid. Here because
    // there is no scheduler, by standing decision (docs/DEPLOYMENT.md): opening
    // the board is what makes them go, and the run is idempotent — the date of
    // the last letter is what decides, not the fact of being called.
    ask("mercury", "remind"),
  ]);

  // Applying is caught too, and for a sharper reason than the polls: this
  // function is what the deposits panel waits on before it will render, so a
  // throw here would take the whole panel off the screen — including deposits
  // that were already sitting there waiting to be matched. It must always
  // resolve, and say what went wrong.
  try {
    const { applied, unmatched } = await applyQuickbooksPayments();

    /*
     * Then the three things that shorten the list, in this order.
     *
     * QuickBooks first: a deposit that has already been identified over there
     * should never be offered to a learned rule, and never announced as
     * something to do. Then the learned rules, so the announcement that follows
     * is only ever about money that genuinely needs a person. Each is caught on
     * its own — none of them is worth taking the panel off the screen for.
     */
    let closed = 0;
    let setAside = 0;
    let linked = 0;
    try {
      closed = await closeDepositsQuickbooksHasIdentified();
    } catch (e) {
      problems.push(
        `matching from QuickBooks: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    // Then Mercury's own side: a credit that names the payment request it
    // settles is that request's money, and no person needs to say so.
    try {
      linked = await linkBankLinesToRequests(problems);
    } catch (e) {
      problems.push(
        `matching to payment requests: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    // Then the card payouts, which have to come after the line above: a
    // payout's bank line must still be unsplit and unconfirmed for this to
    // carve it up.
    let cards = 0;
    try {
      cards = await settleStripePayouts();
    } catch (e) {
      problems.push(
        `splitting a card payout: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    // Checks, before the learned rules and the announcement: a check that is
    // recognised and settled here is not money that needs a person.
    try {
      await receiptsForCheckDeposits(pendingChecks);
    } catch (e) {
      problems.push(
        `check receipts: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    try {
      setAside = await setAsideWhatIsRecognised();
    } catch (e) {
      problems.push(
        `setting aside: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    try {
      await announceNewMoney();
    } catch {
      // Nobody being told is not worth a red line on the board.
    }

    return { applied, unmatched, closed, linked, cards, setAside, problems };
  } catch (e) {
    problems.push(
      `Could not tick the submitted boxes: ${e instanceof Error ? e.message : String(e)}`,
    );
    return {
      applied: 0,
      unmatched: 0,
      closed: 0,
      linked: 0,
      cards: 0,
      setAside: 0,
      problems,
    };
  }
}

// ------------------------------------------------------- the Payments tab ---

/**
 * Every payment on a job, each once, newest first (ADR 0288): hand-entered
 * ones, credits confirmed in Mercury, and links paid but not yet landed.
 * Which is which, and what counts once, is `paymentHistory` in packages/core.
 */
export async function fetchPaymentHistory(
  jobId: string,
): Promise<PaymentHistoryRow[]> {
  const [recorded, deposits, links] = await Promise.all([
    fetchCustomerPayments(jobId),
    supabase
      .from("mercury_deposits")
      .select("id,posted_at,amount_cents,charge_cents,slots,description")
      .eq("job_id", jobId)
      .eq("state", "confirmed"),
    supabase
      .from("mercury_payment_requests")
      .select(
        "id,slot,state,amount_cents,payment_started_at,paid_at," +
          "customer_method,mercury_deposit_id",
      )
      .eq("job_id", jobId),
  ]);
  return paymentHistory({
    recorded: recorded.map((p) => ({
      id: p.id,
      method: p.method,
      amountCents: p.amount_cents,
      receivedOn: p.received_on,
      state: p.state,
      slots: p.slots ?? [],
      towardPlan: p.toward_plan === true,
      checkNumber: p.check_number,
      receiptNumber: p.receipt_number,
      mercuryDepositId: p.mercury_deposit_id,
      mercuryRequestId: p.mercury_payment_request_id ?? null,
    })),
    deposits: unwrap<
      {
        id: string;
        posted_at: string | null;
        amount_cents: number;
        charge_cents: number | null;
        slots: number[] | null;
        description: string | null;
      }[]
    >(deposits).map((d) => ({
      id: d.id,
      postedAt: d.posted_at,
      amountCents: Number(d.amount_cents),
      chargeCents: d.charge_cents == null ? null : Number(d.charge_cents),
      slots: d.slots,
      description: d.description,
    })),
    links: unwrap<
      {
        id: string;
        slot: number | null;
        state: string;
        amount_cents: number | null;
        payment_started_at: string | null;
        paid_at: string | null;
        customer_method: string | null;
        mercury_deposit_id: string | null;
      }[]
    >(links).map((r) => ({
      id: r.id,
      slot: r.slot,
      state: r.state,
      amountCents: r.amount_cents == null ? null : Number(r.amount_cents),
      paymentStartedAt: r.payment_started_at,
      paidAt: r.paid_at,
      customerMethod: r.customer_method,
      mercuryDepositId: r.mercury_deposit_id,
    })),
  });
}

/**
 * Remove a payment entered by mistake (ADR 0288). The database writes who,
 * when and why on the job in the same step, and refuses without a reason.
 */
export async function removeCustomerPayment(
  paymentId: string,
  reason: string,
): Promise<void> {
  const { error } = await supabase.rpc("remove_customer_payment", {
    p_payment: paymentId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

/**
 * A person saying a hand-entered payment is in the bank: a check, a card
 * taken outside Roof Board, a transfer or a financing payout. Paid, and its
 * cleared boxes ticked, the way ticking First or Final Check settles a check
 * (ADR 0288, ADR 0312).
 *
 * Which payments may be marked is `mayMarkInTheBank` in packages/core, and
 * the write is guarded on it too, so cash is never touched. Only this one
 * payment is settled: the boxes are ticked in the caller's name with
 * `moneyFollows: false`, so another payment waiting on the same slot stays
 * waiting. Unticking a box later puts this payment back exactly as it would a
 * check (`checksUnsettledByBox`), unless the bank holds it.
 *
 * If the credit for it is already confirmed on the job, the two are tied
 * (`creditThisPaymentIs`), so the balance counts the money once. A check is
 * never tied here: its own deposit finds it when Mercury shows it.
 */
export async function markPaymentInTheBank(
  paymentId: string,
  profileId: string | null,
): Promise<void> {
  const [before] = unwrap<CustomerPaymentRow[]>(
    await supabase.from("customer_payments").select("*").eq("id", paymentId),
  );
  if (!before || !mayMarkInTheBank(before)) {
    throw new Error(
      "Only a payment still on its way can be marked as in the bank.",
    );
  }
  const rows = unwrap<CustomerPaymentRow[]>(
    await supabase
      .from("customer_payments")
      .update({ state: "paid", paid_at: new Date().toISOString() })
      .eq("id", paymentId)
      .eq("state", "submitted")
      .in("method", [...MARKED_IN_THE_BANK_METHODS])
      .select("*"),
  );
  const payment = rows[0];
  if (!payment) return;
  await tieToConfirmedCredit(payment);
  await tickClearedBoxesFor(
    payment,
    profileId,
    payment.mercury_payment_request_id
      ? `${CUSTOMER_PAYMENT_METHOD_LABEL[payment.method]} payment of ` +
          `${formatMoney(cents(payment.amount_cents))} through a payment link`
      : `Receipt ${payment.receipt_number}`,
  );
}

/**
 * A person saying a payment link's money is already in the bank, before
 * Mercury has shown the credit (ADR 0312).
 *
 * Jonathon, 2026-10-26: "say a payment is already in the bank and it updates
 * that automatically instead of waiting. the intent is to update roofboard
 * asap for jobs where we processed money already for that customer."
 *
 * The record is a `customer_payments` row naming the link, paid as of now,
 * for what the link billed against the link's own payment — what
 * `recordForLink` in packages/core says it is. From then on the balance
 * counts it the way it counts any paid payment entered by hand, and the
 * link's credit, when the bank shows it, is the same money: a credit on the
 * schedule names the same slot, and one off the schedule hands over from
 * the record to the credit (`linkMoneySaidInTheBank`). The customer was
 * receipted by Mercury when they paid, so no receipt is queued here.
 *
 * Pressed twice, the second press finds the record and does nothing more;
 * the database holds one record per link either way. A record that a later
 * untick of the Check box put back to owed is settled again through
 * `markPaymentInTheBank`, like any other.
 */
export async function markLinkInTheBank(
  linkId: string,
  profileId: string | null,
): Promise<void> {
  const [link] = unwrap<
    {
      id: string;
      job_id: string;
      slot: number | null;
      state: string;
      amount_cents: number | null;
      payment_started_at: string | null;
      paid_at: string | null;
      customer_method: string | null;
    }[]
  >(
    await supabase
      .from("mercury_payment_requests")
      .select(
        "id,job_id,slot,state,amount_cents,payment_started_at,paid_at," +
          "customer_method",
      )
      .eq("id", linkId),
  );
  if (!link) throw new Error("That payment link is not on this job any more.");
  const already = unwrap<CustomerPaymentRow[]>(
    await supabase
      .from("customer_payments")
      .select("*")
      .eq("mercury_payment_request_id", linkId),
  );
  if (already[0]) {
    if (already[0].state === "submitted") {
      await markPaymentInTheBank(already[0].id, profileId);
    }
    return;
  }
  const record = recordForLink(
    {
      id: link.id,
      state: String(link.state),
      slot: link.slot == null ? null : Number(link.slot),
      amountCents: link.amount_cents == null ? null : Number(link.amount_cents),
      paymentStartedAt: link.payment_started_at,
      paidAt: link.paid_at,
      customerMethod: link.customer_method,
    },
    already.map((row) => ({
      mercuryRequestId: row.mercury_payment_request_id ?? null,
    })),
  );
  if (!record.allowed) throw new Error(record.why);

  const payment = await insertRow<CustomerPaymentRow>("customer_payments", {
    job_id: link.job_id,
    method: record.method,
    amount_cents: record.amountCents,
    received_on: today(),
    slots: record.slots,
    state: "paid",
    paid_at: new Date().toISOString(),
    source: "person",
    mercury_payment_request_id: link.id,
    created_by: profileId,
  });
  // A credit the bank already showed for this money without naming the link
  // is this record's, so the balance counts it once (`creditThisPaymentIs`).
  await tieToConfirmedCredit(payment);
  const what =
    `${CUSTOMER_PAYMENT_METHOD_LABEL[record.method]} payment of ` +
    `${formatMoney(cents(record.amountCents))} through a payment link`;
  const ticked = await tickClearedBoxesFor(payment, profileId, what);
  if (ticked.length === 0) {
    await insertRow("activities", {
      job_id: link.job_id,
      type: "note",
      direction: "internal",
      body: `${what} marked as in the bank before Mercury showed it.`,
      created_by: profileId,
    });
  }
}

/**
 * Tick the cleared box of each payment this one settles, in the caller's
 * name and for this payment alone (`moneyFollows: false`), and write what
 * was ticked on the job. Returns the labels ticked.
 */
async function tickClearedBoxesFor(
  payment: CustomerPaymentRow,
  profileId: string | null,
  what: string,
): Promise<string[]> {
  const ticked: string[] = [];
  if (payment.slots.length === 0) return ticked;
  const context = await fetchJobPaymentContext(payment.job_id);
  if (!context) return ticked;
  for (const label of labelsOnClearing(payment.slots as PaymentSlot[])) {
    const item = context.items.get(label);
    if (!item || item.status === "done") continue;
    await setChecklistItemStatus(
      {
        id: item.id,
        job_id: payment.job_id,
        label,
        status: item.status as JobChecklistItem["status"],
      },
      "done",
      profileId,
      { moneyFollows: false },
    );
    ticked.push(label);
  }
  if (ticked.length > 0) {
    await insertRow("activities", {
      job_id: payment.job_id,
      type: "note",
      direction: "internal",
      body: `${what} marked as in the bank — ticked ${ticked.join(" and ")}.`,
      created_by: profileId,
    });
  }
  return ticked;
}

/**
 * Tie a payment just marked as in the bank to the credit already confirmed
 * on the job that is the same money. Which credit is `creditThisPaymentIs`
 * in packages/core; the write is guarded on the payment still being untied.
 */
async function tieToConfirmedCredit(
  payment: CustomerPaymentRow,
): Promise<void> {
  const [credits, recorded, links] = await Promise.all([
    supabase
      .from("mercury_deposits")
      .select("id,amount_cents,posted_at,description,slots")
      .eq("job_id", payment.job_id)
      .eq("state", "confirmed"),
    supabase
      .from("customer_payments")
      .select("mercury_deposit_id")
      .eq("job_id", payment.job_id),
    supabase
      .from("mercury_payment_requests")
      .select("mercury_deposit_id")
      .eq("job_id", payment.job_id),
  ]);
  const held = new Set(
    [
      ...unwrap<{ mercury_deposit_id: string | null }[]>(recorded),
      ...unwrap<{ mercury_deposit_id: string | null }[]>(links),
    ]
      .map((row) => row.mercury_deposit_id)
      .filter((id): id is string => Boolean(id)),
  );
  const creditId = creditThisPaymentIs(
    {
      id: payment.id,
      method: payment.method,
      state: payment.state,
      amountCents: payment.amount_cents,
      slots: payment.slots,
      towardPlan: payment.toward_plan === true,
      depositId: payment.mercury_deposit_id,
      mercuryRequestId: payment.mercury_payment_request_id ?? null,
    },
    unwrap<
      {
        id: string;
        amount_cents: number;
        posted_at: string | null;
        description: string | null;
        slots: number[] | null;
      }[]
    >(credits).map((credit) => ({
      id: credit.id,
      amountCents: Number(credit.amount_cents),
      postedAt: credit.posted_at,
      description: credit.description,
      heldElsewhere: held.has(credit.id),
      slots: credit.slots,
    })),
  );
  if (!creditId || creditId === payment.mercury_deposit_id) return;
  await supabase
    .from("customer_payments")
    .update({ mercury_deposit_id: creditId })
    .eq("id", payment.id)
    .is("mercury_deposit_id", null);
}
